<template>
    <a-spin :spinning="loading" size="large" tip="Aguarde, estamos carregando as camadas...">
        
        <div id="map" :style="{ cursor: activeTool === 'streetview' ? 'crosshair' : 'default' }" v-show="!isPreviewMode">
            <HomeContextMenu :map="globalMap" v-if="!!globalMap" @reload-files="loadFileLayers" />
            
            <MapLeftMenu 
                :activeTool="activeTool"
                :isSnapActive="isSnapActive"
                :showVertices="showVertices"
                :isGridVisible="isGridVisible"
                :isDocVisible="isDocVisible"
                :colorSelected="colorSelected"
                @set-tool="setActiveTool"
                @action="handleMapAction"
                @update-color="colorInput"
            />

            <MapLayerSwitcher 
                :map="globalMap" 
                :is-map-editor="true" 
                v-if="!loading" 
                @action="handleMapAction"
                @open-legend="openLegend"
            />

            <a-modal v-model:open="modalFilesOpen" title="Visualizar arquivos" footer="" width="90%" style="max-width: 1100px; top: 4px" :bodyStyle="{ maxHeight: 'calc(100vh - 90px)', overflowY: 'auto' }" @afterOpenChange="onMapFilesModalAfterOpen">
                <div v-if="mapModalFileItems.length" class="map-files-carousel-wrap">
                    <div class="map-files-carousel-inner">
                        <a-button v-if="mapModalFileItems.length > 1" type="text" class="map-files-carousel-arrow map-files-carousel-arrow-prev" @click="mapFilesCarouselPrev">
                            <i class="fas fa-chevron-left" aria-hidden="true"></i>
                        </a-button>
                        <a-carousel ref="mapFilesCarouselRef" effect="fade" class="map-files-carousel">
                            <div v-for="(item, index) in mapModalFileItems" :key="`${item.url}-${index}`" class="map-files-slide">
                                <div class="map-files-slide-inner">
                                    <template v-if="isMapFileImageUrl(item.url)">
                                        <img :src="item.url" :alt="item.name" class="map-files-preview-img" @error="($e) => ($e.target.style.display = 'none')" />
                                    </template>
                                    <template v-else-if="isMapFilePdfUrl(item.url)">
                                        <embed v-if="item.url" :src="item.url" type="application/pdf" class="map-files-preview-pdf" />
                                    </template>
                                    <template v-else>
                                        <div class="map-files-file-card">
                                            <i class="fas fa-file-alt map-files-file-icon"></i>
                                            <span class="map-files-file-type">{{ mapFileTypeLabel(item.name) }}</span>
                                            <a-button type="primary" size="small" @click="openMapFileUrl(item.url)">
                                                <i class="fas fa-external-link-alt"></i> Abrir em nova aba
                                            </a-button>
                                        </div>
                                    </template>
                                    <div class="map-files-slide-actions">
                                        <a :href="item.url" target="_blank" rel="noopener">{{ item.name }}</a>
                                        <a :href="item.url" :download="item.name">
                                            <a-button size="small" type="primary" :icon="h(DownloadOutlined)" />
                                        </a>
                                    </div>
                                </div>
                                <div class="map-files-slide-caption">
                                    {{ mapFileTypeLabel(item.name) }} ({{ index + 1 }}/{{ mapModalFileItems.length }})
                                </div>
                            </div>
                        </a-carousel>
                        <a-button v-if="mapModalFileItems.length > 1" type="text" class="map-files-carousel-arrow map-files-carousel-arrow-next" @click="mapFilesCarouselNext">
                            <i class="fas fa-chevron-right" aria-hidden="true"></i>
                        </a-button>
                    </div>
                </div>
                <div v-else class="map-files-empty">Nenhum arquivo neste ponto.</div>
            </a-modal>

            <div v-show="['polygon', 'linestring'].includes(activeTool) && !isPreviewMode" class="cad-panel">
                <div class="cad-header"><i class="fa fa-drafting-compass"></i> Vetorização Avançada</div>
                <div class="cad-body">
                    <div class="cad-field">
                        <label title="Distância em relação ao último vértice">Distância (m):</label>
                        <input type="number" step="0.01" v-model="cadConfig.distance" placeholder="Ex: 15.5" />
                        <a-button size="small" @click="cadConfig.lockDistance = !cadConfig.lockDistance" :type="cadConfig.lockDistance ? 'primary' : 'default'" :danger="cadConfig.lockDistance">
                            <i :class="cadConfig.lockDistance ? 'fa fa-lock' : 'fa fa-unlock'"></i>
                        </a-button>
                    </div>
                </div>
            </div>

            <div id="sv-modal" v-show="showStreetView && !isPreviewMode" ref="svModalRef">
                <div id="sv-header" ref="svHeaderRef">
                    <span><i class="fa fa-street-view"></i> Street View Sincronizado</span>
                    <button @click="closeStreetView" style="background:none; border:none; color:white; cursor:pointer;"><i class="fa fa-times" /></button>
                </div>
                <div id="pano"></div>
            </div>

            <div v-if="activeLegends.length > 0" class="floating-legend" :style="{ left: legendWindowPos.x + 'px', top: legendWindowPos.y + 'px', zIndex: 2600 }">
                <div class="legend-header" @mousedown="startDragLegendWindow">
                    <span><i class="fa fa-list-ul"></i> Legenda(s)</span>
                    <button @click="activeLegends = []" style="background:none; border:none; color:white; cursor:pointer;"><i class="fa fa-times" /></button>
                </div>
                <div class="legend-body" style="max-height: 400px; overflow-y: auto; background: white; padding: 10px;">
                    <div v-for="legend in activeLegends" :key="legend.id" style="margin-bottom: 15px;">
                        <div style="font-weight: bold; font-size: 12px; margin-bottom: 5px; display: flex; justify-content: space-between;">
                            {{ legend.title }}
                            <button @click="removeLegend(legend.id)" style="background:none; border:none; cursor:pointer; color: red;"><i class="fa fa-trash" /></button>
                        </div>
                        <img v-if="!legend.error" :src="legend.url" alt="Carregando..." @error="legend.error = true" style="max-width:100%;" />
                        <div v-if="legend.error" class="legend-error"><i class="fa fa-exclamation-triangle"></i> Não disponível.</div>
                    </div>
                </div>
            </div>
        </div>

        <a-modal v-model:open="showLabelSetupDialog" title="Configurar Rótulo da Camada" :footer="null">
            <div v-if="currentLabelLayer">
                <label style="font-weight: bold; display: block; margin-bottom: 5px;">Atributo para exibir (Texto):</label>
                <a-select :value="currentLabelLayer.get('labelField') || 'NONE'" @change="(val) => updateLabelProp('labelField', val)" style="width: 100%; margin-bottom: 15px;">
                    <a-select-option value="NONE">-- Nenhum Texto --</a-select-option>
                    <a-select-option v-for="attr in getAttributesFromLayer(currentLabelLayer.get('name'))" :key="attr" :value="attr">{{ attr }}</a-select-option>
                </a-select>
                <div style="display: flex; gap: 15px; margin-bottom: 15px;">
                    <div style="flex: 1;">
                        <label style="font-weight: bold; display: block; margin-bottom: 5px;">Cor da Fonte:</label>
                        <input type="color" :value="currentLabelLayer.get('labelColor') || '#000000'" @input="(e) => updateLabelProp('labelColor', e.target.value)" style="width: 100%; height: 32px; border: 1px solid #ccc; border-radius: 4px; padding: 2px; cursor: pointer;" />
                    </div>
                    <div style="flex: 1;">
                        <label style="font-weight: bold; display: block; margin-bottom: 5px;">Tamanho (px):</label>
                        <input type="number" :value="currentLabelLayer.get('labelSize') || 12" @input="(e) => updateLabelProp('labelSize', parseInt(e.target.value))" min="8" max="72" :style="{width: '100%', height: '32px', padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px'}" />
                    </div>
                </div>
                <label style="font-weight: bold; display: block; margin-bottom: 5px;">Tipo da Fonte:</label>
                <a-select :value="currentLabelLayer.get('labelFont') || 'Arial'" @change="(val) => updateLabelProp('labelFont', val)" style="width: 100%; margin-bottom: 20px;">
                    <a-select-option value="Arial">Arial</a-select-option>
                    <a-select-option value="'Times New Roman'">Times New Roman</a-select-option>
                    <a-select-option value="Courier">Courier</a-select-option>
                </a-select>
                <div style="text-align: right;"><a-button @click="showLabelSetupDialog = false" type="primary" class="btn-success">Concluído</a-button></div>
            </div>
        </a-modal>

        <a-modal v-model:open="showPrePrint" width="85%" style="top: 20px;" footer="" @cancel="handleClosePrintModal" :closable="!isExporting" :maskClosable="!isExporting">
            <h3 v-if="!isPreviewMode"><i class="fa fa-cogs"></i> Configurar Exportação do Mapa</h3>
            <h3 v-else><i class="fa fa-eye"></i> Ajuste e Prévia do Layout Final</h3>
            
            <a-form v-if="!isPreviewMode" :model="prePrintForm" name="prePrintForm" layout="vertical" :colon="false">
                <a-form-item label="Título Superior do Mapa (Opcional):" name="topTitle">
                    <a-input v-model:value="prePrintForm.topTitle" placeholder="Ex: Mapa de Zoneamento - Área Norte" />
                </a-form-item>

                <div style="background:#fafafa; padding: 15px; border-radius: 8px; border: 1px solid #eee;">
                    <h4 style="margin-top:0; border-bottom: 1px solid #ccc; padding-bottom: 5px;">Dados do Documento (Opcional para o Rodapé)</h4>
                    <a-row :gutter="16">
                        <a-col :span="24">
                            <a-form-item label="Título do Rodapé (Carimbo):" name="mapTitle">
                                <a-input v-model:value="prePrintForm.mapTitle" placeholder="Ex: Loteamento Central" />
                            </a-form-item>
                        </a-col>
                    </a-row>
                    <a-row :gutter="16">
                        <a-col :span="24">
                            <a-form-item label="Descrição / Assunto:" name="mapDescription">
                                <a-input v-model:value="prePrintForm.mapDescription" placeholder="Ex: Levantamento topográfico das quadras" />
                            </a-form-item>
                        </a-col>
                    </a-row>
                    <a-row :gutter="16">
                        <a-col :span="12">
                            <a-form-item label="Responsável:" name="responsible">
                                <a-input v-model:value="prePrintForm.responsible" />
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item label="Observações:" name="observations">
                                <a-input v-model:value="prePrintForm.observations" placeholder="Ex: Datum SIRGAS 2000" />
                            </a-form-item>
                        </a-col>
                    </a-row>
                    <a-row :gutter="16">
                        <a-col :span="24">
                            <a-form-item label="Logotipo (Opcional):">
                                <a-upload :file-list="prePrintFileList" :before-upload="beforeUpload" @remove="handleRemove" :max-count="1" accept="image/png, image/jpeg">
                                    <a-button>Selecionar logo</a-button>
                                </a-upload>
                            </a-form-item>
                        </a-col>
                    </a-row>
                    <a-row :gutter="16" v-if="prePrintForm.showLegend" style="margin-top: 15px; border-top: 1px solid #ddd; padding-top: 15px;">
                        <a-col :span="24">
                            <label style="font-weight: bold; font-size: 13px; display: block; margin-bottom: 8px;">Quais legendas devem ser impressas?</label>
                            <a-checkbox-group v-model:value="prePrintForm.legendLayers" style="width: 100%;">
                                <a-row>
                                    <a-col :span="8" v-for="name in visibleLayerNames" :key="name">
                                        <a-checkbox :value="name">{{ name }}</a-checkbox>
                                    </a-col>
                                </a-row>
                            </a-checkbox-group>
                        </a-col>
                    </a-row>
                </div>
                
                <div class="modal-buttons mt-3">
                    <a-button @click="handleClosePrintModal" danger type="primary">Cancelar</a-button>
                    <a-button @click="enterPreviewMode" class="btn-info" type="primary"><i class="fa fa-magic"></i> Ir para Prévia do Mapa</a-button>
                </div>
            </a-form>

            <div v-show="isPreviewMode">
                <div style="background: #f8f9fa; padding: 10px 20px; border-radius: 8px; margin-bottom: 15px; border: 1px solid #ddd;">
                    <a-row :gutter="16" align="middle">
                        <a-col :span="6">
                            <label style="font-weight: bold; font-size: 12px; display: block; margin-bottom: 4px;">Alterar Escala do Mapa:</label>
                            <a-select v-model:value="prePrintForm.scale" @change="applyMapScale" style="width: 100%" size="small" :disabled="isExporting">
                                <a-select-option value="current">Livre (Ajuste com o mouse)</a-select-option>
                                <a-select-option value="500">1:500</a-select-option>
                                <a-select-option value="1000">1:1.000</a-select-option>
                                <a-select-option value="2000">1:2.000</a-select-option>
                                <a-select-option value="5000">1:5.000</a-select-option>
                                <a-select-option value="10000">1:10.000</a-select-option>
                            </a-select>
                        </a-col>
                        <a-col :span="5">
                            <a-checkbox v-model:checked="prePrintForm.useStamp" style="font-weight: bold; font-size: 12px; margin-top: 20px;" :disabled="isExporting">Apresentar Carimbo</a-checkbox>
                        </a-col>
                        <a-col :span="5">
                            <a-checkbox v-model:checked="prePrintForm.showScale" @change="toggleScaleVisibility" style="font-weight: bold; font-size: 12px; margin-top: 20px;" :disabled="isExporting">Apresentar Escala</a-checkbox>
                        </a-col>
                        <a-col :span="4">
                            <a-checkbox v-model:checked="prePrintForm.showNorth" style="font-weight: bold; font-size: 12px; margin-top: 20px;" :disabled="isExporting">Apresentar Norte</a-checkbox>
                        </a-col>
                        <a-col :span="4">
                            <a-checkbox v-model:checked="prePrintForm.showLegend" style="font-weight: bold; font-size: 12px; margin-top: 20px;" :disabled="isExporting">Apresentar Legenda</a-checkbox>
                        </a-col>
                    </a-row>
                </div>

                <div class="print-preview-layout">
                    <div class="d-flex flex-column justify-content-between my-2">
                        <div class="d-flex flex-column gap-3">
                            <a-button @click="downloadExport('jpg')" class="btn-dark" type="primary" :disabled="isExporting"><i class="fa fa-image" /> Baixar JPG</a-button>
                            <a-button @click="downloadExport('png')" class="btn-dark" type="primary" :disabled="isExporting"><i class="fa fa-image" /> Baixar PNG</a-button>
                            <a-button @click="downloadExport('pdf')" class="btn-success" type="primary" :disabled="isExporting"><i class="fa fa-file-pdf-o" /> Baixar PDF</a-button>
                            <a-button @click="printMap" class="btn-info" type="primary"><i class="fa fa-print" /> Imprimir</a-button>
                        </div>

                        <a-button @click="exitPreviewMode" class="btn-secondary" type="primary" :disabled="isExporting"><i class="fa fa-arrow-left"></i> Voltar aos Textos</a-button>
                    </div>

                        <div class="print-preview-board">
                            <h2 v-if="prePrintForm.topTitle" style="text-align: center; margin: 0 0 10px 0; color: black; font-weight: bold;">{{ prePrintForm.topTitle }}</h2>
                            <div id="preview-boundary">
                                <div id="preview-map-container" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; width: 100%; height: 100%;"></div>
                                <div v-if="prePrintForm.showNorth" style="position: absolute; top: 15px; right: 15px; z-index: 100; pointer-events: none; text-align: center;">
                                    <svg width="50" height="60" viewBox="0 0 100 120">
                                        <polygon points="50,0 70,60 50,100 30,60" fill="rgba(255,255,255,0.9)" stroke="#333" stroke-width="2"/>
                                        <polygon points="50,0 70,60 50,100" fill="#333"/>
                                        <text x="50" y="118" font-family="Arial" font-size="28" font-weight="bold" fill="black" text-anchor="middle" stroke="white" stroke-width="2">N</text>
                                        <text x="50" y="118" font-family="Arial" font-size="28" font-weight="bold" fill="black" text-anchor="middle">N</text>
                                    </svg>
                                </div>
                                <div v-if="prePrintForm.showLegend && prePrintLegendItems.length > 0" class="draggable-print-legend" :style="{ left: `${legendPreviewPos.x}px`, top: `${legendPreviewPos.y}px` }" @mousedown.stop="startLegendDrag" title="Arraste para posicionar a legenda no papel">
                                    <div style="font-weight:bold; font-size: 14px; border-bottom: 1px solid #ccc; margin-bottom: 5px; cursor: move; text-align: center; background: #f0f0f0; padding: 2px;">LEGENDA</div>
                                    <div
                                        v-for="legend in prePrintLegendItems"
                                        :key="legend.name"
                                        class="print-legend-item"
                                    >
                                        <div class="print-legend-title">{{ legend.name }}</div>
                                        <img
                                            v-if="legend.url && !legend.error"
                                            :src="legend.url"
                                            :alt="`Legenda da camada ${legend.name}`"
                                            @error="markPrintLegendError(legend.name)"
                                            class="print-legend-image"
                                        />
                                        <span
                                            v-else
                                            :style="{
                                                display: 'inline-block',
                                                width: '15px',
                                                height: '10px',
                                                background: legend.fillColor,
                                                border: `1px solid ${legend.strokeColor}`,
                                                marginRight: '5px',
                                                verticalAlign: 'middle'
                                            }"
                                        ></span>
                                    </div>
                                </div>
                            </div>

                            <div v-if="prePrintForm.useStamp" style="border: 2px solid #000; border-top: none; display: flex; padding: 5px; color: black;">
                                <div style="flex: 1;">
                                    <h4 style="margin-bottom: 3px; font-weight: bold; color: black;">TÍTULO: {{ prePrintForm.mapTitle || 'Mapa Cadastral' }}</h4>
                                    <div style="font-size: 13px; margin-bottom: 1px;"><b>Descrição:</b> {{ prePrintForm.mapDescription || '-' }}</div>
                                    <div style="font-size: 13px; margin-bottom: 1px;"><b>Responsável:</b> {{ prePrintForm.responsible || '-' }}</div>
                                    <div style="font-size: 13px;">
                                        <b>Data:</b> {{ new Date().toLocaleDateString('pt-BR') }} &nbsp;|&nbsp;
                                        <b>Obs:</b> {{ prePrintForm.observations || '-' }}
                                    </div>
                                </div>
                                <div v-if="prePrintForm.logo.src" style="width: 120px; text-align: center; border-left: 2px solid #000; padding-left: 10px; display: flex; align-items: center; justify-content: center;">
                                    <img :src="prePrintForm.logo.src" style="max-width: 100%; max-height: 80px;" />
                                </div>
                            </div>
                        </div>
                </div>
            </div>
        </a-modal>

        <a-modal v-model:open="showSaveDialog" footer="">
            <h3><i class="fa fa-save" /> Gravar Alterações / Exportar</h3>
            <p style="font-size: 13px; color: #555;">Selecione a camada:</p>
            <a-select v-model:value="dirtyLayerSelected" style="width:100%; font-weight:bold; margin-bottom: 10px;">
                <a-select-option v-for="layer in dirtyLayers" :key="layer.get('name')" :value="layer.get('name')">
                    {{ layer.get('name') }} {{ layer.get('isDirty') ? '(Pendente)' : '' }}
                </a-select-option>
            </a-select>
            <label style="font-size: 12px;">Formato de Exportação Local:</label>
            <a-select v-model:value="exportFormatSelected" style="width:100%; margin-bottom: 15px;">
                <a-select-option value="geojson">GeoJSON</a-select-option>
                <a-select-option value="kml">KML</a-select-option>
            </a-select>
            <div style="display: flex; flex-direction: column; gap: 10px;">
                <a-button :disabled="!dirtyLayerSelected || !isLayerDirty(dirtyLayerSelected)" @click="executeSaveLayer(null, true)" type="primary" class="btn-warning" style="height:fit-content; padding:12px; border:none; border-radius:5px; font-weight:bold;">
                    <i class="fa fa-database" /> Salvar alterações no Banco de Dados
                </a-button>
                <a-button :disabled="!dirtyLayerSelected" @click="saveLayerOnComputer" type="primary" class="btn-info" style="height:fit-content">
                    <i class="fa fa-download" /> Exportar Camada para o Computador
                </a-button>
                <a-button @click="showSaveDialog = false" type="primary" danger>Cancelar</a-button>
            </div>
        </a-modal>

        <a-modal v-model:open="showDialog" footer="">
            <h3><i class="fa fa-pencil"></i> Escolher Camada</h3>
            <label>Onde deseja inserir esta geometria?</label> <br />
            <small>São exibidas apenas as geometrias compatíveis.</small>
            <a-select v-model:value="selectedLayer" :options="selectList" class="mt-1" style="width: 100%; font-weight: bold; background: #e9ecef;" :getPopupContainer="trigger => trigger.parentNode" />
            <div class="modal-buttons">
                <a-button @click="showDialog = false" danger type="primary">Cancelar</a-button>
                <a-button @click="confirmDrawClick" type="primary" class="btn-success">Iniciar Desenho</a-button>
            </div>
        </a-modal>

        <a-modal v-model:open="showPasteDialog" footer="">
            <h3><i class="fa fa-clipboard"></i> Colar Geometria</h3>
            <label>Onde deseja colar a geometria copiada?</label>
            <a-select v-model:value="pasteOptions.layer" :options="selectList" style="width: 100%; font-weight: bold; background: #e9ecef;" :getPopupContainer="trigger => trigger.parentNode" />
            <div class="modal-buttons">
                <a-button @click="showPasteDialog = false" danger type="primary">Cancelar</a-button>
                <a-button @click="confirmPasteClick" type="primary" class="btn-success">Confirmar Colar</a-button>
            </div>
        </a-modal>

        <div v-if="showTextDialog" class="custom-modal" style="z-index: 2300;">
            <h3><i class="fa fa-font"></i> Inserir Texto Livre</h3>
            <label>Texto:</label>
            <input type="text" v-model="textOptions.texto" autocomplete="off" placeholder="Ex: Lote 12">
            <div style="display: flex; gap: 15px; margin-top: 10px;">
                <div style="flex: 1;"><label>Tamanho:</label><input type="number" v-model="textOptions.fontSize" min="8" max="100"></div>
                <div style="flex: 1;"><label>Cor:</label><input type="color" v-model="textOptions.textColor" style="width: 100%; height: 35px; border-radius: 4px; padding: 2px;"></div>
            </div>
            <label style="margin-top: 10px;">Fonte:</label>
            <select v-model="textOptions.fontFamily" style="width: 100%; padding: 8px; border-radius: 4px; border: 1px solid #ccc;">
                <option value="Arial">Arial</option>
                <option value="'Times New Roman'">Times</option>
                <option value="Courier">Courier</option>
            </select>
            <div class="modal-buttons">
                <a-button @click="showTextDialog = false" danger type="primary">Cancelar</a-button>
                <a-button @click="confirmDrawTextClick" class="btn-success" type="primary">Fixar no Mapa</a-button>
            </div>
        </div>

        <a-modal v-model:open="showTableDialog" width="90%" :footer="null" :closable="false" :bodyStyle="{ padding: '0' }">
            <a-spin :spinning="isTableLoading" size="large" tip="Processando dados da tabela...">
                <div style="padding: 20px;">
                    <h3 id="table-title" style="display: flex; justify-content: space-between; align-items: center; margin-top: 0;">
                        <span><i class="fa fa-table"></i> Dados: {{ tableLayerName }}</span>
                        <a-button @click="showTableDialog = false" shape="circle" danger><i class="fa fa-times"></i></a-button>
                    </h3>
                    <button @click="openTableExport" class="btn-success" style="padding: 8px; border:none; border-radius:4px; font-weight:bold; cursor:pointer; color:white; width: 100%;">
                        <i class="fa fa-magic"></i> Criar Nova Camada com Resultados ({{ filteredTableFeatures.length }} registros)
                    </button>
                    <div class="table-container">
                        <table id="data-table">
                            <thead id="data-table-head">
                                <tr><th v-for="col in tableColumns" :key="col">{{ col }}</th></tr>
                                <tr class="tr-filter">
                                    <th v-for="col in tableColumns" :key="'filter-'+col">
                                        <input type="text" v-model="tableFilters[col]" placeholder="Ex: > 100 ou Texto" class="col-filter-input" title="Use >, <, >=, <=, ==, != para números" />
                                    </th>
                                </tr>
                            </thead>
                            <tbody id="data-table-body">
                                <tr v-for="(item, index) in filteredTableFeatures" :key="index">
                                    <td v-for="col in tableColumns" :key="col">
                                        {{ item.props[col] !== null && item.props[col] !== undefined ? item.props[col] : '' }}
                                    </td>
                                </tr>
                                <tr v-if="filteredTableFeatures.length === 0">
                                    <td :colspan="tableColumns.length" style="text-align: center; padding: 15px; color: #999;">Nenhum registro atende aos filtros atuais.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </a-spin>
        </a-modal>

        <a-modal v-model:open="showTableExportDialog" title="Estilizar Nova Camada a partir da Tabela" footer="">
            <label>Nome da Nova Camada:</label>
            <a-input v-model:value="tableExportData.name" style="margin-bottom: 10px;" />
            <div style="display: flex; gap: 15px; margin-bottom: 15px;">
                <div style="flex: 1;"><label>Cor (Fundo):</label><input type="color" v-model="tableExportData.fill" style="width: 100%; height: 35px; border:none; padding:0;" /></div>
                <div style="flex: 1;"><label>Cor (Borda):</label><input type="color" v-model="tableExportData.stroke" style="width: 100%; height: 35px; border:none; padding:0;" /></div>
            </div>
            <div class="modal-buttons" style="display: flex; justify-content: flex-end; gap: 10px;">
                <a-button @click="showTableExportDialog = false" type="primary" danger>Cancelar</a-button>
                <a-button @click="confirmTableExport" type="primary" class="btn-success">Criar Camada</a-button>
            </div>
        </a-modal>

        <a-modal v-model:open="showAtrributeDialog" width="50%" footer="">
            <h3><i class="fa fa-list-alt" /> Editar Atributos</h3>
            <a-form layout="vertical">
                <a-row :gutter="12">
                    <a-col :span="8" v-for="(value, key) in attributesForm" :key="key">
                        <a-form-item :label="key">
                            <a-input v-model:value="attributesForm[key]" :disabled="key === 'gid'" />
                        </a-form-item>
                    </a-col>
                </a-row>
            </a-form>
            <div class="modal-buttons">
                <a-button @click="cancelAttrDialog" danger type="primary">Cancelar</a-button>
                <a-button @click="saveAttrs" class="btn-success">Salvar</a-button>
            </div>
        </a-modal>

    </a-spin>
</template>

<script setup>
import { h, onMounted, ref, computed, markRaw, shallowRef, reactive, nextTick, toRaw } from "vue";
import { DownloadOutlined } from "@ant-design/icons-vue";
import Map from "ol/Map";
import UndoRedo from "ol-ext/interaction/UndoRedo";
import View from "ol/View";
import * as turf from '@turf/turf';
import 'jsts/dist/jsts.min.js';
import GeoJSON from "ol/format/GeoJSON";
import KML from "ol/format/KML";
import { Graticule } from "ol/layer";
import Stroke from "ol/style/Stroke";
import VectorLayer from "ol/layer/Vector";
import LayerGroup from "ol/layer/Group";
import Select from "ol/interaction/Select";
import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import { Circle, Text, Icon } from "ol/style";
import Point from "ol/geom/Point";
import { MultiPoint, LineString, Polygon } from "ol/geom";
import { getLength } from "ol/sphere";
import { Draw, Modify, Snap, Translate } from "ol/interaction";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import { transform, getPointResolution } from "ol/proj";
import jsPDF from "jspdf";
import { saveAs } from 'file-saver';
import ScaleLine from "ol/control/ScaleLine";

import { createEditableLayer } from "../../../open-layers/helpers";
import HomeContextMenu from "../MapsOpenLayers/Components/HomeContextMenu.vue";

import MapLeftMenu from './components/MapLeftMenu.vue';
import MapLayerSwitcher from "../MapsOpenLayers/Components/MapLayerSwitcher.vue";
import { defaults as defaultControls } from 'ol/control'
import { getDefaultMapView } from "../../../open-layers/map-defaults.js";
import { openNotification } from "../../../utils/utilities.js";

const GEO_SERVER_URL = window.geoServerUrl;

const globalMap = ref(null);
let mapLayers = ref([]);

const documentsSource = markRaw(new VectorSource());
const imagesSource = markRaw(new VectorSource());
const documentsLayer = new VectorLayer({ source: documentsSource, zIndex: 2500 });
const imagesLayer = new VectorLayer({ source: imagesSource, zIndex: 2500 });

const documentsClicked = ref([]);
const imagesClicked = ref([]);
const modalFilesOpen = ref(false);
const mapFilesCarouselRef = ref(null);

const activeTool = ref('');
const globalAttributeKeys = ref(new Set());
let clipboard = null;
const undoInteraction = new UndoRedo();
const isExporting = ref(false);
const snapInteraction = ref(null);
const isSnapActive = ref(false);
const showVertices = ref(false);
const loading = ref(true);
const pendingDrawType = ref(null);
const selectList = ref([]);
const selectedLayer = ref(null);
const layerStates = ref([]);
const dirtyLayers = ref([]);
const dirtyLayerSelected = ref(null);
const exportFormatSelected = ref('geojson');
const edits = ref([]);

const cadConfig = reactive({ distance: 0, lockDistance: false, angle: 0, lockAngle: false });

const activeLegends = ref([]);
const attributesForm = ref({});
let globalScaleLine = null;

const showPrePrint = ref(false);
const isPreviewMode = ref(false);

const prePrintForm = reactive({ 
    useStamp: true, 
    showScale: true, 
    showNorth: true, 
    showLegend: true,
    scale: 'current',
    legendLayers: [], 
    topTitle: '', 
    mapTitle: 'Mapa Cadastral', 
    mapDescription: '', 
    responsible: '', 
    observations: '', 
    logo: new Image() 
});
const prePrintFileList = ref([]);

const legendPreviewPos = reactive({ x: 0, y: 0 });
let isDraggingLegendPrint = false;
let legendDragStartX = 0;
let legendDragStartY = 0;

const legendWindowPos = reactive({ x: 80, y: 80 });
let isDraggingLegendWindow = false;
let legendWindowDragStartX = 0;
let legendWindowDragStartY = 0;

const startDragLegendWindow = (event) => {
    isDraggingLegendWindow = true;
    legendWindowDragStartX = event.clientX - legendWindowPos.x;
    legendWindowDragStartY = event.clientY - legendWindowPos.y;

    const onDrag = (e) => {
        if (isDraggingLegendWindow) {
            legendWindowPos.x = e.clientX - legendWindowDragStartX;
            legendWindowPos.y = e.clientY - legendWindowDragStartY;
        }
    };

    const onStop = () => {
        isDraggingLegendWindow = false;
        document.removeEventListener('mousemove', onDrag);
        document.removeEventListener('mouseup', onStop);
    };

    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', onStop);
};

const removeLegend = (id) => {
    activeLegends.value = activeLegends.value.filter(l => l.id !== id);
};

const selectedFeature = ref(null);
const layersModel = ref({});
const colorSelected = ref('#3399CC');

const showDialog = ref(false);
const showAtrributeDialog = ref(false);
const showSaveDialog = ref(false);
const showLabelSetupDialog = ref(false);
const currentLabelLayer = shallowRef(null);
const showPasteDialog = ref(false);
const pasteOptions = reactive({ layer: '' });

const isGridVisible = ref(false);
const isDocVisible = ref(false);
const showTextDialog = ref(false);
const textOptions = ref({ texto: '', fontSize: 16, textColor: '#000000', fontFamily: 'Arial', layer: '' });

const showStreetView = ref(false);
const svModalRef = ref(null);
const svHeaderRef = ref(null);

const showTableDialog = ref(false);
const showTableExportDialog = ref(false);
const isTableLoading = ref(false);
const tableLayerName = ref('');
const tableColumns = ref([]);
const tableFeatures = ref([]);
const tableFilters = ref({});
const tableExportData = ref({ name: 'Camada Filtrada', fill: '#ffcc33', stroke: '#cc9900' });
const currentInteraction = ref(null);
const graticule = new Graticule({ showLabels: true, visible: false, wrapX: false, stroke: new Stroke({ color: 'rgba(0,0,0,0.5)', lineDash: [0.5, 4] }) });
let svPanorama = null;
const svMarkerSource = new VectorSource();
const svMarkerLayer = new VectorLayer({ source: svMarkerSource, zIndex: 9999, visible: false });
const svFeature = new Feature({ geometry: new Point([0,0]) });
svMarkerSource.addFeature(svFeature);

const clearInteractions = () => {
    if (!!currentInteraction.value && globalMap.value) {
        globalMap.value.removeInteraction(currentInteraction.value);
    }
    currentInteraction.value = null;
};

const hexToRgba = (hex, alpha) => `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}, ${alpha})`;

const formatLength = (line) => { 
    const length = getLength(line); 
    return length > 1000 ? (Math.round((length / 1000) * 100) / 100) + ' km' : (Math.round(length * 100) / 100) + ' m'; 
};

const calculateAzimuth = (pt1, pt2) => { 
    const dx = pt2[0] - pt1[0]; 
    const dy = pt2[1] - pt1[1]; 
    let theta = Math.atan2(dx, dy); 
    let angle = (theta * 180 / Math.PI); 
    if (angle < 0) angle += 360; 
    return angle; 
};

const getTableName = (layer) => {
    if (!layer) return null;
    const geoTable = layer.get('geo_table');
    if (geoTable) return geoTable;
    const gsLayer = layer.get('geo_server_layer'); 
    if (gsLayer && gsLayer.includes(':')) {
        return gsLayer.split(':')[1]; 
    }
    return layer.get('name');
};

const sourceHasFeature = (source, feature) => {
    if (!source || !feature) return false;
    if (typeof source.hasFeature === 'function') return source.hasFeature(feature);
    if (typeof source.getFeatures === 'function') return source.getFeatures().includes(feature);
    return false;
};

const findOwningLayer = (feature, preferredLayer = null) => {
    if (preferredLayer?.getSource && sourceHasFeature(preferredLayer.getSource(), feature)) {
        return preferredLayer;
    }

    const mapLayer = mapLayers.value.find(layer => layer.getSource && sourceHasFeature(layer.getSource(), feature));
    if (mapLayer) return mapLayer;

    if (sourceHasFeature(imagesLayer?.getSource?.(), feature)) return imagesLayer;
    if (sourceHasFeature(documentsLayer?.getSource?.(), feature)) return documentsLayer;
    if (sourceHasFeature(annotationsSource, feature)) return annotationsLayer;

    return null;
};

const isValidLayerForType = (layer, type) => {
    if (!layer.getSource || typeof layer.getSource().getFeatures !== 'function') return false;
    const features = layer.getSource().getFeatures();
    if (features.length === 0) return true;
    const geomType = features[0].getGeometry().getType();
    if (type === 'Point' && !geomType.includes('Point')) return false;
    if (type === 'LineString' && !geomType.includes('LineString')) return false;
    if (type === 'Polygon' && !geomType.includes('Polygon')) return false;
    return true;
};

const getLegendUrl = (layer) => {
    const gServerLayerName = layer.get('geo_server_layer');
    if (!gServerLayerName) return null;
    let workspace = '';
    if (gServerLayerName.includes(':')) {
        workspace = gServerLayerName.split(':')[0] + '/';
    }
    let baseUrl = GEO_SERVER_URL.startsWith('http') ? GEO_SERVER_URL : (window.location.origin + GEO_SERVER_URL);

    if (window.location.protocol === 'https:' && baseUrl.startsWith('http://')) {
        baseUrl = window.location.origin;
    }

    baseUrl = baseUrl.replace(/\/$/, '');
    return `${baseUrl}/geoserver/${workspace}wms?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&LAYER=${gServerLayerName}`;
};

const openMapFileUrl = (url) => window.open(url, '_blank');
const mapFilesCarouselPrev = () => mapFilesCarouselRef.value?.prev?.();
const mapFilesCarouselNext = () => mapFilesCarouselRef.value?.next?.();
const isMapFileImageUrl = (url = '') => /\.(png|jpg|jpeg|webp|gif)(\?|$)/i.test(url);
const isMapFilePdfUrl = (url = '') => /\.pdf(\?|$)/i.test(url);

const onMapFilesModalAfterOpen = (open) => {
    if (!open) return;
    nextTick(() => { mapFilesCarouselRef.value?.goTo?.(0, false) });
};

const mapFileTypeLabel = (name = '') => {
    const ext = (name.split('.').pop() || '').toLowerCase();
    if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) return 'Imagem';
    if (ext === 'pdf') return 'PDF';
    if (['doc', 'docx'].includes(ext)) return 'Documento';
    if (['xls', 'xlsx'].includes(ext)) return 'Planilha';
    if (ext === 'txt') return 'Texto';
    return 'Arquivo';
};

const mapFilePublicUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return path.startsWith('/') ? path : `/${path}`;
};

const constraintGeometry = (coords, geom, type) => {
    if (!geom) {
        geom = type === 'Polygon' ? new Polygon([coords[0]]) : new LineString(coords);
    }
    
    let points = type === 'Polygon' ? coords[0] : coords;

    if (points.length > 1) {
        let last = points[points.length - 1]; 
        let prev = points[points.length - 2]; 

        let dx = last[0] - prev[0];
        let dy = last[1] - prev[1];
        let rawDist = Math.sqrt(dx * dx + dy * dy);

        if (rawDist > 0.01) {
            let angle = Math.atan2(dy, dx);
            let dist = rawDist;
            let locked = false;

            if (cadConfig.lockAngle) {
                angle = (cadConfig.angle * Math.PI) / 180;
                locked = true;
            }

            if (cadConfig.lockDistance && globalMap.value) {
                const projection = globalMap.value.getView().getProjection();
                const pointResolution = getPointResolution(projection, 1, prev, 'm');
                dist = cadConfig.distance / pointResolution; 
                
                const p1 = transform(prev, 'EPSG:3857', 'EPSG:4326');
                const bearing = (angle * 180 / Math.PI) * -1 + 90;
                const destination = turf.destination(turf.point(p1), cadConfig.distance / 1000, bearing, {units: 'kilometers'});
                const newPoint3857 = transform(destination.geometry.coordinates, 'EPSG:4326', 'EPSG:3857');
                
                last[0] = newPoint3857[0];
                last[1] = newPoint3857[1];
                locked = true;
            }

            if (locked && !cadConfig.lockDistance) {
                last[0] = prev[0] + dist * Math.cos(angle);
                last[1] = prev[1] + dist * Math.sin(angle);
            }
        }
    }
    
    geom.setCoordinates(coords);
    return geom;
};

function generateLayerStyle(color, labelField, labelColor = '#000000', labelSize = 12, labelFont = 'Arial') {
    return function(feature) {
        let styles = [];

        if (feature.get('isText')) {
            const size = feature.get('fontSize') || '16';
            const font = feature.get('fontFamily') || 'Arial';
            const tColor = feature.get('textColor') || '#000000';
            styles.push(new Style({
                text: new Text({ text: feature.get('texto'), font: `bold ${size}px ${font}`, fill: new Fill({ color: tColor }), stroke: new Stroke({ color: '#fff', width: 3 }), offsetY: -5 })
            }));
            return styles;
        }

        let textConfig = null;
        let dynamicLabelField = feature.get('customLabelField') || labelField;

        if (dynamicLabelField && dynamicLabelField !== 'NONE') {
            let val = feature.get(dynamicLabelField);
            if (val !== undefined && val !== null) {
                textConfig = new Text({ text: String(val), font: `bold ${labelSize}px ${labelFont}`, fill: new Fill({ color: labelColor }), stroke: new Stroke({ color: '#fff', width: 3 }), overflow: true });
            }
        }

        const fFill = feature.get('color') || hexToRgba(color, 0.3);
        const fStroke = feature.get('strokeColor') || color;

        styles.push(new Style({
            fill: new Fill({ color: fFill }),
            stroke: new Stroke({ color: fStroke, width: 2 }),
            image: new Circle({ radius: 5, fill: new Fill({ color: fFill }) }),
            text: textConfig
        }));

        if (showVertices.value) {
            let coords = [];
            const geometry = feature.getGeometry();
            if (geometry) {
                const type = geometry.getType();
                if (type === 'Point') coords.push(geometry.getCoordinates());
                else if (type === 'MultiPoint') coords.push(...geometry.getCoordinates());
                else if (type === 'LineString' || type === 'LinearRing') coords.push(...geometry.getCoordinates());
                else if (type === 'Polygon') geometry.getCoordinates().forEach(c => coords.push(...c));
                else if (type === 'MultiLineString') geometry.getCoordinates().forEach(c => coords.push(...c));
                else if (type === 'MultiPolygon') geometry.getCoordinates().forEach(p => p.forEach(c => coords.push(...c)));

                if (coords.length > 0) {
                    styles.push(new Style({
                        image: new Circle({ radius: 4, fill: new Fill({ color: '#ff0000' }), stroke: new Stroke({ color: '#fff', width: 1 }) }),
                        geometry: new MultiPoint(coords)
                    }));
                }
            }
        }
        return styles;
    };
}

function measureStyle(feature) {
    if (feature.get('isText')) return generateLayerStyle('#3399CC', 'NONE')(feature);
    const color = feature.get('color') || '#ffcc33';
    const styles = [new Style({
        fill: new Fill({color: hexToRgba(color, 0.4)}),
        stroke: new Stroke({color: '#ffcc33', width: 3}),
        image: new Circle({radius: 6, fill: new Fill({color: '#ffcc33'})})
    })];

    if (feature.getGeometry()) {
        const type = feature.getGeometry().getType();
        let ringsOrLines = [];
        
        if (type === 'LineString') ringsOrLines = [feature.getGeometry().getCoordinates()];
        else if (type === 'MultiLineString') ringsOrLines = feature.getGeometry().getCoordinates();
        else if (type === 'Polygon') ringsOrLines = feature.getGeometry().getCoordinates();
        else if (type === 'MultiPolygon') ringsOrLines = feature.getGeometry().getCoordinates().flat();

        ringsOrLines.forEach(coords => {
            for (let i = 0; i < coords.length - 1; i++) {
                const pt1 = coords[i], pt2 = coords[i + 1];
                const line = new LineString([pt1, pt2]);
                const azimuth = calculateAzimuth(pt1, pt2);
                const midPoint = [(pt1[0] + pt2[0]) / 2, (pt1[1] + pt2[1]) / 2];
                styles.push(new Style({ 
                    geometry: new Point(midPoint), 
                    text: new Text({ 
                        text: `${formatLength(line)} | ${azimuth.toFixed(1)}°`, 
                        font: 'bold 12px Arial', 
                        fill: new Fill({color: '#000'}), 
                        stroke: new Stroke({color: '#fff', width: 3}), 
                        offsetY: -10 
                    }) 
                }));
            }
        });
    }
    return styles;
}

const selectInteraction = new Select({style: measureStyle});

const updateLayerStyle = (layer) => {
    layer.setStyle(generateLayerStyle(
        layer.get('strokeColor') || '#3399CC',
        layer.get('labelField') || 'NONE',
        layer.get('labelColor') || '#000000',
        layer.get('labelSize') || 12,
        layer.get('labelFont') || 'Arial'
    ));
    layer.changed();
};

const updateSvMarkerStyle = (heading) => {
    const pegmanSvg = encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32"><path fill="#fbbc05" stroke="#fff" stroke-width="0.5" d="M12 11.5c-1.6 0-3-1.4-3-3s1.4-3 3-3 3 1.4 3 3-1.4 3-3 3zm5 2.5h-2v-1c0-1.6-1.4-3-3-3h-4c-1.6 0-3 1.4-3 3v1H7c-1.1 0-2 .9-2 2v5h2v7h2.5v-7h5v7H17v-7h2v-5c0-1.1-.9-2-2-2z"/></svg>');
    const fovSvg = encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="80" height="80"><path d="M50 50 L15 5 A 50 50 0 0 1 85 5 Z" fill="rgba(66, 133, 244, 0.5)" stroke="rgba(66, 133, 244, 0.8)" stroke-width="1"/></svg>');
    svFeature.setStyle([ new Style({ image: new Icon({ src: 'data:image/svg+xml;charset=utf-8,' + fovSvg, rotation: heading * Math.PI / 180, anchor: [0.5, 0.5] }) }), new Style({ image: new Icon({ src: 'data:image/svg+xml;charset=utf-8,' + pegmanSvg, anchor: [0.5, 0.8] }) }) ]);
};

const annotationsSource = markRaw(new VectorSource());
const annotationsLayer = new VectorLayer({
    source: annotationsSource,
    name: 'Anotações (Textos)',
    zIndex: 3000,
    style: measureStyle
});

const printLegendErrors = reactive({});

const prePrintLegendItems = computed(() => {
    return prePrintForm.legendLayers
        .map(name => mapLayers.value.find(layer => layer.get('name') === name))
        .filter(Boolean)
        .map(layer => {
            const url = getLegendUrl(layer);
            const source = layer.getSource?.();
            const features = source?.getFeatures?.() || [];

            const colors = new globalThis.Map();

            features.forEach(feature => {
                const strokeColor = feature.get('strokeColor') || layer.get('strokeColor') || '#3399CC';
                const fillColor = feature.get('color') || layer.get('fillColor') || hexToRgba(strokeColor, 0.3);

                const key = `${fillColor}-${strokeColor}`;

                if (!colors.has(key)) {
                    colors.set(key, {
                        name: layer.get('name'),
                        strokeColor,
                        fillColor,
                    });
                }
            });

            if (colors.size === 0) {
                const strokeColor = layer.get('strokeColor') || '#3399CC';

                return {
                    name: layer.get('name'),
                    url,
                    error: !!printLegendErrors[layer.get('name')],
                    strokeColor,
                    fillColor: layer.get('fillColor') || hexToRgba(strokeColor, 0.3),
                };
            }

            const [firstStyle] = Array.from(colors.values());
            return {
                name: layer.get('name'),
                url,
                error: !!printLegendErrors[layer.get('name')],
                strokeColor: firstStyle.strokeColor,
                fillColor: firstStyle.fillColor,
            };
        });
});

const markPrintLegendError = (name) => {
    printLegendErrors[name] = true;
};

const fitLegendImage = (width, height, maxWidth, maxHeight) => {
    if (!width || !height) {
        return { width: 0, height: 0 };
    }

    const ratio = Math.min(maxWidth / width, maxHeight / height, 1);

    return {
        width: Math.max(1, Math.floor(width * ratio)),
        height: Math.max(1, Math.floor(height * ratio)),
    };
};

const printMap = async () => {
    isExporting.value = true;

    try {
        await nextTick();

        if (globalMap.value) {
            globalMap.value.updateSize();
        }

        await new Promise(resolve => setTimeout(resolve, 300));

        window.print();
    } finally {
        setTimeout(() => {
            isExporting.value = false;

            if (globalMap.value) {
                globalMap.value.updateSize();
            }
        }, 500);
    }
};

const mapModalFileItems = computed(() => {
    const items = []
    documentsClicked.value.forEach((f) => {
        items.push({ name: f.get('name'), url: mapFilePublicUrl(f.get('path')) })
    })
    imagesClicked.value.forEach((f) => {
        items.push({ name: f.get('name'), url: mapFilePublicUrl(f.get('path')) })
    })
    return items
});

const visibleLayerNames = computed(() => {
    return mapLayers.value
        .filter(l => l.getVisible() && l.get('name') && !['Imagens', 'Documentos'].includes(l.get('name')))
        .map(l => l.get('name'));
});

const filteredTableFeatures = computed(() => {
    return tableFeatures.value.filter(item => {
        for (const key in tableFilters.value) {
            let filterValue = tableFilters.value[key];
            if (filterValue && filterValue.trim() !== '') {
                filterValue = filterValue.trim();
                let cellValue = item.props[key];

                const match = filterValue.match(/^(>=|<=|>|<|==|!=)\s*(.+)$/);
                if (match) {
                    const operator = match[1];
                    const targetValue = match[2];
                    const numCell = Number(cellValue);
                    const numTarget = Number(targetValue);
                    if (!isNaN(numCell) && !isNaN(numTarget)) {
                        if (operator === '>' && !(numCell > numTarget)) return false;
                        if (operator === '<' && !(numCell < numTarget)) return false;
                        if (operator === '>=' && !(numCell >= numTarget)) return false;
                        if (operator === '<=' && !(numCell <= numTarget)) return false;
                        if (operator === '==' && !(numCell == numTarget)) return false;
                        if (operator === '!=' && !(numCell != numTarget)) return false;
                        continue;
                    } else {
                        if (operator === '==' && cellValue != targetValue) return false;
                        if (operator === '!=' && cellValue == targetValue) return false;
                    }
                } else {
                    if (!String(cellValue || '').toLowerCase().includes(filterValue.toLowerCase())) return false;
                }
            }
        }
        return true;
    });
});

const getAttributesFromLayer = (layerName) => {
    const l = mapLayers.value.find(ly => ly.get('name') === layerName);
    if (!l || !l.getSource) return [];

    const feats = l.getSource().getFeatures();
    if (feats.length === 0) return [];

    const keys = new Set();
    Object.keys(feats[0].getProperties()).forEach(k => {
        if (!['geometry', 'color', 'strokeColor', 'isText', 'customLabelField'].includes(k)) keys.add(k);
    });
    return Array.from(keys);
};

const flattenLayerTree = (layers = []) => {
    return layers.flatMap((layer) => {
        if (!layer) return [];
        if (layer instanceof LayerGroup) {
            return flattenLayerTree(layer.getLayers().getArray());
        }
        return [layer];
    });
};

const configureEditableMapLayer = (layer) => {
    if (!layer) return;

    layer.set('isMain', layer.get('visible'));
    layer.set('isDirty', false);
    layer.set('strokeColor', '#cca833');
    layer.set('fillColor', hexToRgba('#cca833', 0.3));
    layer.set('labelField', 'NONE');
    layer.set('labelColor', '#000000');
    layer.set('labelSize', 12);
    layer.set('labelFont', 'Arial');

    const source = typeof layer.getSource === 'function' ? layer.getSource() : null;
    if (!source) return;

    source.on('featuresloadend', () => {
        const state = layerStates.value.find(l => l.ol_uid === layer.ol_uid);
        if(state) state.loaded = true;

        const features = source.getFeatures();
        if (features.length > 0) {
            const props = features[0].getProperties();
            const result = {};
            Object.keys(props).forEach(key => {
                if (key === 'geometry') return;
                if (key === 'tableName') result[key] = props[key]
                else result[key] = '';
            });
            layersModel.value[layer.ol_uid] = result
        }
    });

    setTimeout(() => {
        source.on(['addfeature', 'removefeature'], () => {
            const state = layerStates.value.find(l => l.ol_uid === layer.ol_uid);
            if (state && state.loaded) markLayerDirty(layer);
        });
    }, 1000);
};

const updateDirtyLayers = () => {
    dirtyLayers.value = mapLayers.value.filter(layer => layer.get('isDirty'));
};

const markLayerDirty = (layer, bool = true) => {
    toRaw(layer).set('isDirty', bool);
    mapLayers.value = [...mapLayers.value];
    updateDirtyLayers();
};

const isLayerDirty = (layerName) => {
    if (!layerName) return false;
    const layer = mapLayers.value.find(l => l.get('name') === layerName) 
               || [documentsLayer, imagesLayer].find(l => l.get('name') === layerName);
               
    return layer ? layer.get('isDirty') === true : false;
};

const layerNameChange = (e, layer) => {
    layer.set('name', e.target.value);
    markLayerDirty(layer);
};

const removeLayer = (layer) => {
    if (!layer.get('isMain') && globalMap.value) {
        globalMap.value.removeLayer(layer);
        mapLayers.value = mapLayers.value.filter(l => l.ol_uid !== layer.ol_uid);
        openNotification('success', 'Camada removida da visualização.');
    } else {
        openNotification('warning', 'Camadas principais não podem ser removidas.');
    }
};

const moveLayer = (layer, direction) => {
    if (!globalMap.value) return;
    const layersCollection = globalMap.value.getLayers();
    const layersArray = layersCollection.getArray();
    const index = layersArray.indexOf(layer);

    if (index < 0) return;

    if (direction === 'up' && index < layersArray.length - 1) {
        const nextLayer = layersArray[index + 1];
        layersCollection.setAt(index, nextLayer);
        layersCollection.setAt(index + 1, layer);
    } 
    else if (direction === 'down' && index > 1) { 
        const prevLayer = layersArray[index - 1];
        layersCollection.setAt(index, prevLayer);
        layersCollection.setAt(index - 1, layer);
    }
};

const changeVisibility = (layer) => {
    layer.set('loading', true);
    layer.setVisible(!layer.getVisible());
    layer.set('loading', false);
    layer.changed();
};

const updateLabelProp = (prop, value) => {
    if (currentLabelLayer.value) {
        currentLabelLayer.value.set(prop, value);
        updateLayerStyle(currentLabelLayer.value);
    }
};

const openLabelSetup = (layer) => {
    currentLabelLayer.value = layer;
    showLabelSetupDialog.value = true;
};

const beforeUpload = (file) => {
    prePrintFileList.value = [file];
    let reader = new FileReader();
    reader.onload = () => {
        prePrintForm.logo = new Image();
        prePrintForm.logo.src = reader.result;
    };
    reader.readAsDataURL(file);
    return false;
};

const handleRemove = () => {
    prePrintForm.logo.src = '';
    prePrintFileList.value = [];
};

const openPrintModal = () => {
    isPreviewMode.value = false;
    showPrePrint.value = true;
};

const handleClosePrintModal = () => {
    showPrePrint.value = false;
    isExporting.value = false;
    
    if (isPreviewMode.value) {
        if (globalMap.value) globalMap.value.setTarget(null);
        isPreviewMode.value = false;
        
        setTimeout(() => {
            if (globalMap.value) {
                globalMap.value.setTarget(document.getElementById('map'));
                globalMap.value.updateSize();
            }
        }, 300); 
    } else {
        isPreviewMode.value = false;
    }
};

const handleMapAction = (actionName) => {
    switch (actionName) {
        case 'undoClick': (undoInteraction.length() > 0) ? undoInteraction.undo() : ''; break;
        case 'redoClick': (undoInteraction.hasRedo()) ? undoInteraction.redo() : ''; break;
        case 'svClick': svClick(); break;
        case 'snapClick': snapClick(); break;
        case 'verticesClick': verticesClick(); break;
        case 'copyClick': copyClick(); break;
        case 'pasteClick': pasteClick(); break;
        case 'editDataClick': editDataClick(); break;
        case 'deleteClick': deleteClick(); break;
        case 'showDocumentsClick': showDocumentsClick(); break;
        case 'gridClick': gridClick(); break;
        case 'openPrintModal': openPrintModal(); break;
        case 'openSaveMenu': openSaveMenu(); break;
        case 'openAdvancedFilter': openAdvancedFilter(); break;
    }
};

const setActiveTool = (toolName) => {
    clearInteractions();
    activeTool.value = toolName;
    
    if (toolName === 'pan') {
        selectInteraction.getFeatures().clear();
    } else if (toolName === 'polygon') {
        openDrawLayerSelector('Polygon');
    } else if (toolName === 'linestring') {
        openDrawLayerSelector('LineString');
    } else if (toolName === 'point') {
        openDrawLayerSelector('Point');
    } else if (toolName === 'translate') {
        translateClick();
    } else if (toolName === 'modify') {
        modifyClick();
    } else if (toolName === 'scissors') {
        scissorsClick();
    } else if (toolName === 'offset') {
        offsetClick();
    } else if (toolName === 'text') {
        drawTextClick();
    }

    if (isSnapActive.value) {
        setTimeout(() => toggleSnap(), 100);
    }
};

const panClick = () => { setActiveTool('pan'); }
const svClick = () => { setActiveTool('streetview'); openNotification('info', 'Street View Ativo: Clique em uma rua no mapa.'); }
const closeStreetView = () => { showStreetView.value = false; svMarkerLayer.setVisible(false); panClick(); }

const translateClick = () => {
    if (selectInteraction.getFeatures().getLength() === 0) { 
        openNotification('error', 'Selecione primeiro uma geometria para mover.'); 
        panClick(); 
        return; 
    }
    currentInteraction.value = new Translate({ features: selectInteraction.getFeatures() });
    currentInteraction.value.on('translateend', (event) => {
        event.features.forEach(feature => {
            let targetLayer = findOwningLayer(feature);
            if (targetLayer && targetLayer !== annotationsLayer) {
                markLayerDirty(targetLayer);
                const existingEditIndex = edits.value.findIndex(e => e.feature === feature);
                if (existingEditIndex >= 0) {
                    edits.value[existingEditIndex].type = 'U';
                } else {
                    edits.value.push({ type: 'U', table: getTableName(targetLayer), name: targetLayer.get('name'), feature: feature });
                }
            }
        });
    });
    globalMap.value.addInteraction(currentInteraction.value);
}

const modifyClick = () => {
    const features = selectInteraction.getFeatures();
    if (features.getLength() === 0) { alert('Selecione uma geometria!'); panClick(); return; }

    const modify = new Modify({ features: features });
    modify.on('pointermove', () => modify.getOverlay().getSource().clear());
    modify.on('modifystart', () => modify.getOverlay().getSource().clear());
    
    modify.on('modifyend', (event) => {
        modify.getOverlay().getSource().clear();
        event.features.forEach(feature => {
            let targetLayer = findOwningLayer(feature);
            if (!targetLayer || targetLayer === annotationsLayer) return;
            markLayerDirty(targetLayer);
            const existingEditIndex = edits.value.findIndex(e => e.feature === feature);
            if (existingEditIndex >= 0) {
                if (edits.value[existingEditIndex].type !== 'I') edits.value[existingEditIndex].type = 'U';
            } else {
                edits.value.push({ type: 'U', table: getTableName(targetLayer), name: targetLayer.get('name'), feature: feature });
            }
        });
    });

    currentInteraction.value = modify;
    globalMap.value.addInteraction(currentInteraction.value);
}

const toggleSnap = () => {
    if (snapInteraction.value && globalMap.value) {
        globalMap.value.removeInteraction(snapInteraction.value);
        snapInteraction.value = null;
    }
    
    if (isSnapActive.value && mapLayers.value.length > 0) {
        const targetLayer = mapLayers.value.find(l => l.get('name') === selectedLayer.value) || mapLayers.value.find(l => l.get('isMain')) || mapLayers.value[0];
        if(targetLayer && targetLayer.getSource) {
            snapInteraction.value = new Snap({ source: targetLayer.getSource(), pixelTolerance: 15 });
            globalMap.value.addInteraction(snapInteraction.value);
        }
    }
};

const snapClick = () => { isSnapActive.value = !isSnapActive.value; toggleSnap(); }

const verticesClick = () => {
    showVertices.value = !showVertices.value;
    mapLayers.value.forEach(l => {
        if(l.getSource) {
            updateLayerStyle(l);
            l.getSource().changed();
        }
    });
}

const offsetClick = () => {
    const features = selectInteraction.getFeatures();
    if (features.getLength() === 0) { openNotification('warning', 'Selecione primeiro a geometria.'); panClick(); return; }

    const distStr = prompt("Digite a distância da paralela em metros (use negativo para encolher):", "2");
    if (distStr === null || distStr.trim() === "") { panClick(); return; }

    const distMeters = parseFloat(distStr.replace(',', '.'));
    if (isNaN(distMeters)) { openNotification('error', 'Valor numérico inválido.'); panClick(); return; }

    try {
        const feature = features.item(0);
        const format = new GeoJSON();
        const geojson = format.writeFeatureObject(feature);
        let offsetGeoJSON = null;

        if (geojson.geometry.type.includes('Polygon')) {
            const jstsLib = window.jsts;
            if (!jstsLib) throw new Error("A biblioteca JSTS não foi encontrada.");
            const reader = new jstsLib.io.GeoJSONReader();
            const geom = reader.read(geojson.geometry);
            const bufferParams = new jstsLib.operation.buffer.BufferParameters();
            bufferParams.setJoinStyle(jstsLib.operation.buffer.BufferParameters.JOIN_MITRE);
            const bufferedGeom = jstsLib.operation.buffer.BufferOp.bufferOp(geom, distMeters, bufferParams);
            const writer = new jstsLib.io.GeoJSONWriter();
            offsetGeoJSON = { type: 'Feature', geometry: writer.write(bufferedGeom), properties: geojson.properties };
        } else if (geojson.geometry.type.includes('LineString')) {
            offsetGeoJSON = turf.lineOffset(geojson, distMeters, { units: 'meters' });
        }

        if (offsetGeoJSON) {
            const newFeatureOL = format.readFeature(offsetGeoJSON);
            feature.setGeometry(newFeatureOL.getGeometry());
            feature.changed();
            
            const targetLayer = findOwningLayer(feature);
            if (targetLayer && targetLayer !== annotationsLayer) {
                markLayerDirty(targetLayer);
                const existingEditIndex = edits.value.findIndex(e => e.feature === feature);
                if (existingEditIndex >= 0) {
                    if (edits.value[existingEditIndex].type !== 'I') edits.value[existingEditIndex].type = 'U';
                } else {
                    edits.value.push({ type: 'U', table: getTableName(targetLayer), name: targetLayer.get('name'), feature: feature });
                }
            }
            openNotification('success', `Offset de ${distMeters}m aplicado.`);
        }
        panClick();
    } catch (error) { console.error("Erro no Offset:", error); openNotification('error', 'Erro ao processar geometria.'); panClick(); }
};

const openLegend = (layer) => {
    const url = getLegendUrl(layer);
    if (!url) {
        openNotification('warning', 'Esta camada não possui referência WMS no GeoServer.');
        return;
    }
    
    const layerUid = layer.ol_uid;
    
    if (!activeLegends.value.find(l => l.id === layerUid)) {
        activeLegends.value.push({
            id: layerUid,
            title: layer.get('name'),
            url: url,
            error: false
        });
    }
};

const startLegendDrag = (event) => {
    if (isExporting.value) return;
    isDraggingLegendPrint = true;
    legendDragStartX = event.clientX - legendPreviewPos.x;
    legendDragStartY = event.clientY - legendPreviewPos.y;
    
    const onLegendDrag = (e) => {
        if (!isDraggingLegendPrint) return;
        const container = document.getElementById('preview-boundary');
        const legendBox = document.querySelector('.draggable-print-legend');
        if (!container) return;
        let newX = e.clientX - legendDragStartX;
        let newY = e.clientY - legendDragStartY;
        const rect = container.getBoundingClientRect();
        const legendRect = legendBox?.getBoundingClientRect();
        const legendWidth = legendRect?.width || 50;
        const legendHeight = legendRect?.height || 50;
        if (newX < 0) newX = 0;
        if (newY < 0) newY = 0;
        const maxX = Math.max(0, rect.width - legendWidth);
        const maxY = Math.max(0, rect.height - legendHeight);
        if (newX > maxX) newX = maxX;
        if (newY > maxY) newY = maxY;
        legendPreviewPos.x = newX;
        legendPreviewPos.y = newY;
    };
    
    const stopLegendDrag = () => {
        isDraggingLegendPrint = false;
        document.removeEventListener('mousemove', onLegendDrag);
        document.removeEventListener('mouseup', stopLegendDrag);
    };
    
    document.addEventListener('mousemove', onLegendDrag);
    document.addEventListener('mouseup', stopLegendDrag);
};

const scissorsClick = () => {
    const selectedFeatures = selectInteraction.getFeatures();
    if (selectedFeatures.getLength() !== 1) {
        openNotification('warning', 'Atenção: Primeiro, selecione UMA geometria (Polígono) usando a ferramenta de Seleção.');
        panClick(); return;
    }

    const targetFeature = selectedFeatures.item(0);
    const geomType = targetFeature.getGeometry().getType();
    if (!geomType.includes('Polygon')) {
        openNotification('warning', 'A tesoura só funciona em Polígonos.');
        return;
    }

    let targetLayer = findOwningLayer(targetFeature);
    if (!targetLayer) return;

    openNotification('info', 'Modo Tesoura: Desenhe uma linha cruzando o polígono de fora a fora.');

    currentInteraction.value = new Draw({
        source: new VectorSource(), 
        type: 'LineString',
        style: new Style({ stroke: new Stroke({ color: '#ff0000', width: 3, lineDash: [5, 5] }) })
    });

    currentInteraction.value.on('drawend', (event) => {
        const lineFeature = event.feature;
        globalMap.value.removeInteraction(currentInteraction.value);
        
        try {
            const jsts = window.jsts;
            if (!jsts) throw new Error("A biblioteca JSTS não está carregada.");
            
            const format = new GeoJSON();
            const reader = new jsts.io.GeoJSONReader();
            const writer = new jsts.io.GeoJSONWriter();

            const polyGeom = reader.read(format.writeGeometryObject(targetFeature.getGeometry()));
            const lineGeom = reader.read(format.writeGeometryObject(lineFeature.getGeometry()));
            const unionGeom = polyGeom.getBoundary().union(lineGeom);
            
            const polygonizer = new jsts.operation.polygonize.Polygonizer();
            polygonizer.add(unionGeom);
            const polygons = polygonizer.getPolygons();

            let newFeatures = [];
            const originalProps = targetFeature.getProperties();
            delete originalProps.geometry; 
            
            for (let i = polygons.iterator(); i.hasNext(); ) {
                const p = i.next();
                if (polyGeom.contains(p.getInteriorPoint())) {
                    const newOlGeom = format.readGeometry(writer.write(p)); 
                    const newFeat = new Feature(newOlGeom);
                    newFeat.setProperties({ ...originalProps });
                    newFeat.setId(undefined);
                    newFeat.set('gid', 'NOVO');
                    newFeat.set('isNew', true);
                    newFeatures.push(newFeat);
                }
            }

            if (newFeatures.length > 1) {
                const source = targetLayer.getSource();
                source.removeFeature(targetFeature); 
                source.addFeatures(newFeatures); 

                const tableName = getTableName(targetLayer);
                const existingEditIndex = edits.value.findIndex(e => e.feature === targetFeature);
                if (existingEditIndex >= 0) {
                    edits.value[existingEditIndex].type = 'D';
                } else {
                    edits.value.push({ type: 'D', table: tableName, name: targetLayer.get('name'), feature: targetFeature });
                }

                newFeatures.forEach(nf => {
                    edits.value.push({ type: 'I', table: tableName, name: targetLayer.get('name'), feature: nf });
                });

                markLayerDirty(targetLayer);
                selectInteraction.getFeatures().clear();
                openNotification('success', `Sucesso! Polígono dividido em ${newFeatures.length} partes.`);
            } else {
                openNotification('warning', 'O corte falhou. A linha não cruzou completamente as bordas.');
            }
        } catch (e) {
            console.error(e);
            openNotification('error', 'Erro ao calcular o corte.');
        }
        setTimeout(() => panClick(), 100);
    });

    globalMap.value.addInteraction(currentInteraction.value);
};

const openDrawLayerSelector = (type) => {
    pendingDrawType.value = type;
    selectList.value = mapLayers.value
        .filter(l => layerStates.value.find(layer => l.ol_uid === layer.ol_uid)?.loaded)
        .filter(l => isValidLayerForType(l, type))
        .map(l => ({ value: l.get('name'), label: l.get('name') }));
    showDialog.value = true;
};

const confirmDrawClick = () => {
    const targetLayer = mapLayers.value.find(l => l.get('name') === selectedLayer.value);
    if (!targetLayer || !targetLayer.getSource) return openNotification('error', 'Selecione uma camada válida.');

    showDialog.value = false;

    const drawConfig = { 
        source: targetLayer.getSource(), 
        type: pendingDrawType.value, 
        style: measureStyle
    };

    if (cadConfig.lockDistance || cadConfig.lockAngle) {
        drawConfig.geometryFunction = (coords, geom) => constraintGeometry(coords, geom, pendingDrawType.value);
    }

    currentInteraction.value = new Draw(drawConfig);

    currentInteraction.value.on('drawend', async (event) => {
        const feature = event.feature
        globalMap.value.removeInteraction(currentInteraction.value);
        feature.setProperties({'gid': 'NOVO', 'isNew': true});

        edits.value.push({ type: 'I', table: getTableName(targetLayer), name: targetLayer.get('name'), feature: feature });
        markLayerDirty(targetLayer);
        openNotification('info', `Nova geometria desenhada.`);
    });

    globalMap.value.addInteraction(currentInteraction.value);
};

const drawTextClick = () => {
    textOptions.value.texto = '';
    showTextDialog.value = true;
};

const confirmDrawTextClick = () => {
    if (!textOptions.value.texto.trim()) return openNotification('warning', 'Digite um texto para inserir.');

    if (!globalMap.value.getLayers().getArray().includes(annotationsLayer)) {
        globalMap.value.addLayer(annotationsLayer);
    }

    showTextDialog.value = false;
    currentInteraction.value = new Draw({
        source: annotationsSource,
        type: 'Point',
        style: new Style({ image: new Circle({ radius: 0 }) })
    });

    currentInteraction.value.on('drawend', (e) => {
        const feature = e.feature;
        globalMap.value.removeInteraction(currentInteraction.value);

        feature.setProperties({
            isText: true,
            texto: textOptions.value.texto,
            fontSize: textOptions.value.fontSize,
            textColor: textOptions.value.textColor,
            fontFamily: textOptions.value.fontFamily
        });

        openNotification('success', 'Texto adicionado ao mapa.');
        setTimeout(() => { panClick(); }, 100);
    });

    globalMap.value.addInteraction(currentInteraction.value);
    openNotification('info', 'Clique no mapa onde deseja fixar o texto.');
};

const copyClick = () => {
    const features = selectInteraction.getFeatures();
    if (features.getLength() > 0) {
        clipboard = features.item(0).clone();
        openNotification('success', 'Geometria copiada com sucesso!');
    } else {
        openNotification('warning', 'Selecione uma geometria para copiar.');
    }
};

const pasteClick = () => {
    if (!clipboard) {
        openNotification('warning', 'Nenhuma geometria copiada.');
        return;
    }

    selectList.value = mapLayers.value
        .filter(l => l.getSource && typeof l.getSource().getFeatures === 'function')
        .map(l => ({ value: l.get('name'), label: l.get('name') }));
        
    if (selectList.value.length > 0) {
        pasteOptions.layer = selectList.value[0].value;
    }
    
    showPasteDialog.value = true;
};

const confirmPasteClick = () => {
    const targetLayer = mapLayers.value.find(l => l.get('name') === pasteOptions.layer);
    if (!targetLayer || !targetLayer.getSource) return openNotification('error', 'Selecione uma camada válida.');

    showPasteDialog.value = false;
    openNotification('info', 'Clique no mapa no local exato onde deseja colar a geometria.');

    currentInteraction.value = new Draw({
        source: new VectorSource(),
        type: 'Point',
        style: new Style({ image: new Circle({ radius: 0 }) })
    });

    currentInteraction.value.on('drawend', (e) => {
        const clickCoord = e.feature.getGeometry().getCoordinates();
        globalMap.value.removeInteraction(currentInteraction.value);

        const newFeature = clipboard.clone();
        const geom = newFeature.getGeometry();

        if (geom) {
            const extent = geom.getExtent();
            const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];

            const dx = clickCoord[0] - center[0];
            const dy = clickCoord[1] - center[1];

            geom.translate(dx, dy);
            newFeature.setGeometry(geom);
        }

        newFeature.setId(undefined);

        const props = { ...newFeature.getProperties() };
        const chavesParaLimpar = ['gid', 'id', 'objectid', 'fid', 'geom'];
        chavesParaLimpar.forEach(chave => {
            delete props[chave];
            newFeature.unset(chave);
        });

        newFeature.setProperties({
            ...props,
            'gid': 'NOVO',
            'isNew': true
        });

        targetLayer.getSource().addFeature(newFeature);

        edits.value.push({
            type: 'I',
            table: getTableName(targetLayer),
            name: targetLayer.get('name'),
            feature: newFeature
        });

        markLayerDirty(targetLayer);
        targetLayer.changed();

        openNotification('success', `Geometria colada na camada: ${targetLayer.get('name')}`);

        setTimeout(() => {
            selectInteraction.getFeatures().clear();
            selectedFeature.value = null;
            panClick();
        }, 150);
    });

    globalMap.value.addInteraction(currentInteraction.value);
};

const editDataClick = () => {
    const features = selectInteraction.getFeatures();
    const filtered = {};
    const chavesIgnoradas = ['geometry', 'propertyId', 'color', 'isText', 'texto', 'fontSize', 'fontFamily', 'textColor', 'tableName', 'geom', 'strokeColor', 'customLabelField', 'isNew'];

    if (features.getLength() !== 1) { 
        openNotification('error', 'Selecione uma geometria!'); 
        return; 
    }

    const featureToEditAttr = features.item(0);
    selectedFeature.value = featureToEditAttr;

    let targetLayer = findOwningLayer(featureToEditAttr, selectedLayer.value);
    if (targetLayer) {
        selectedLayer.value = targetLayer;
    }

    let baseProps = {};
    if (targetLayer && targetLayer.ol_uid && layersModel.value[targetLayer.ol_uid]) {
        baseProps = { ...layersModel.value[targetLayer.ol_uid] };
    } else if (targetLayer && targetLayer.getSource) {
        const referenceFeature = targetLayer.getSource().getFeatures().find(f => !f.get('isNew'));
        if (referenceFeature) {
            Object.keys(referenceFeature.getProperties()).forEach(k => baseProps[k] = '');
        }
    }

    const props = { ...baseProps, ...featureToEditAttr.getProperties() };

    for (let key in props) {
        if (chavesIgnoradas.includes(key)) continue;
        filtered[key] = props[key] ?? '';
    }

    if (!filtered.hasOwnProperty('gid') || !filtered['gid']) {
        filtered['gid'] = featureToEditAttr.get('gid') || 'NOVO';
    }

    attributesForm.value = filtered;
    showAtrributeDialog.value = true;
};

const saveAttrs = async () => {
    if (!selectedFeature.value) return

    Object.entries(attributesForm.value).forEach(([key, val]) => {
        let parsed = val
        if (parsed !== '' && !isNaN(parsed)) parsed = Number(parsed)
        selectedFeature.value.set(key, parsed)
    })

    const isNewFeature = selectedFeature.value.get('isNew') === true;
    const editedFeatureIndex = edits.value.findIndex(e => e.feature === selectedFeature.value);

    if (editedFeatureIndex >= 0) {
        edits.value[editedFeatureIndex].feature = selectedFeature.value;
    } else {
        edits.value.push({
            type: isNewFeature ? 'I' : 'U',
            table: getTableName(findOwningLayer(selectedFeature.value, selectedLayer.value)),
            name: findOwningLayer(selectedFeature.value, selectedLayer.value)?.get('name'),
            feature: selectedFeature.value
        })
    }

    const ownerLayer = findOwningLayer(selectedFeature.value, selectedLayer.value);
    if (ownerLayer) {
        selectedLayer.value = ownerLayer;
        markLayerDirty(ownerLayer);
    }
    openNotification('success', 'Atributos adicionados à fila de gravação!');
    showAtrributeDialog.value = false;
};

const cancelAttrDialog = () => { showAtrributeDialog.value = false; }

const deleteClick = async () => {
    if (!selectedFeature.value) { openNotification('error', 'Selecione uma geometria para excluir'); return; }

    let targetLayer = findOwningLayer(selectedFeature.value, selectedLayer.value);

    if (!targetLayer || !targetLayer.getSource) return;

    const feature = selectedFeature.value;
    const source = targetLayer.getSource();
    source.removeFeature(feature);

    if (targetLayer !== annotationsLayer) {
        const editedFeatureIndex = edits.value.findIndex(e => e.feature === feature);
        if (editedFeatureIndex >= 0) {
            edits.value[editedFeatureIndex].type = 'D';
        } else {
            edits.value.push({
                type: 'D', table: getTableName(targetLayer), name: targetLayer.get('name'), feature: feature
            })
        }

        markLayerDirty(targetLayer);
    }

    selectInteraction.getFeatures().clear();
    selectedFeature.value = null;
    selectedLayer.value = null;
    openNotification('success', 'Geometria deletada. Lembre-se de salvar.');
    panClick();
};

const colorInput = (value) => {
    colorSelected.value = value;
    if (typeof selectInteraction !== 'undefined') {
        selectInteraction.getFeatures().forEach(feature => { 
            feature.set('color', value); 
            feature.changed(); 
        });
    }
};

const showDocumentsClick = () => {
    documentsLayer.setVisible(!documentsLayer.getVisible());
    imagesLayer.setVisible(!imagesLayer.getVisible());
    isDocVisible.value = !isDocVisible.value;
};

const gridClick = () => {
    const newValue = !graticule.getVisible()
    graticule.setVisible(newValue)
    isGridVisible.value = newValue
};

const openSaveMenu = () => {
    updateDirtyLayers();
    dirtyLayerSelected.value = dirtyLayers.value[0]?.get('name') ?? null;
    showSaveDialog.value = true;
};

const saveLayerClick = async () => {
    await executeSaveLayer(null, true)
    updateDirtyLayers();
};

const saveLayerOnComputer = async () => {
    let targetName = dirtyLayerSelected.value;
    let format = exportFormatSelected.value;
    let ext = format; 
    let nome = prompt("Introduza o nome para o novo arquivo:", `${targetName.replace(/\s+/g, '_')}.${ext}`);
    
    if (nome) {
        if (!nome.endsWith(`.${ext}`)) nome += `.${ext}`;
        await executeSaveLayer(nome, false, format);
    }

    dirtyLayerSelected.value = null;
    updateDirtyLayers();
};

const executeSaveLayer = async (filename, isOriginal, format = 'geojson') => {
    const targetName = dirtyLayerSelected.value;
    const layerToSave = mapLayers.value.find(l => l.get('name') === targetName) || [documentsLayer, imagesLayer].find(l => l.get('name') === targetName);
                        
    if (!layerToSave) return;

    if (isOriginal) {
        const formatGeo = new GeoJSON();
        const pendingEdits = edits.value.filter(e => e.name === targetName);
        
        const payload = pendingEdits.map(item => {
            const rawGeoJson = formatGeo.writeFeatureObject(item.feature, { featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326', });
            const cleanProps = {};
            const ignoredKeys = [ 'geometry', 'geom', 'color', 'strokeColor', 'customLabelField', 'isDirty', 'isMain', 'tableName', 'isText', 'texto', 'fontSize', 'fontFamily', 'textColor', 'layer_id', 'geometry_gid', 'isNew', 'reference', 'audit_event_key', 'bbox', 'features', 'id' ];
            
            if (rawGeoJson.properties) {
                Object.keys(rawGeoJson.properties).forEach(key => {
                    if (!ignoredKeys.includes(key)) cleanProps[key] = rawGeoJson.properties[key];
                });
            }

            let featureId = null;
            if (typeof item.feature.getId === 'function') featureId = item.feature.getId(); 

            let gid = item.feature.get('gid') || cleanProps.gid || null;
            if (!gid && featureId) gid = String(featureId).includes('.') ? featureId.split('.')[1] : featureId;
            let id = item.feature.get('id') || cleanProps.id || featureId || null;


            if (gid === 'NOVO' || item.type === 'I') {
                gid = null;
                id = null;
                delete cleanProps.gid;
                delete cleanProps.id;
            } else {
                cleanProps.gid = gid;
            }

            return {
                type: item.type, 
                table: getTableName(layerToSave), 
                gid: gid, 
                id: id,
                feature: { type: "Feature", id: featureId || gid, geometry: rawGeoJson.geometry, properties: cleanProps }
            };
        });

        if (payload.length === 0) {
            return openNotification('info', 'Não há dados válidos para salvar.');
        }

        try {
            let response;
            if (targetName === 'Imagens' || targetName === 'Documentos') {
                response = await window.axios.post('/api/map-actions/layers/file/update-geometry', payload);
            } else {
                const table = payload[0]?.table; 
                if (table) {
                    response = await window.axios.post(`/api/maps/layers/${table}/geometry`, payload);
                }
            }

            edits.value.forEach(e => { if (e.name === targetName && e.feature) e.feature.unset('isNew'); });
            edits.value = edits.value.filter(e => e.name !== targetName);
            layerToSave.set('isDirty', false);
            showSaveDialog.value = false;
            openNotification('success', 'Alterações salvas no banco de dados!');
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro interno de servidor.';
            openNotification('error', `Falha ao salvar: ${msg}`);
        }

    } else {
        if(!layerToSave.getSource) return;
        const features = layerToSave.getSource().getFeatures();
        
        if (format === 'geojson') {
            const geojsonStr = new GeoJSON().writeFeatures(features, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857', decimals: 6 });
            const blob = new Blob([geojsonStr], { type: 'application/json' });
            saveAs(blob, filename);
            
        } else if (format === 'kml') {
            const kmlStr = new KML().writeFeatures(features, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' });
            const blob = new Blob([kmlStr], { type: 'application/vnd.google-earth.kml+xml' });
            saveAs(blob, filename);
        }
        showSaveDialog.value = false;
    }
};

const openDataTable = (layer) => {
    if(!layer.getSource) return openNotification('warning', 'Camada não suportada');
    const features = layer.getSource().getFeatures();
    if (features.length === 0) return openNotification('warning', 'A camada está vazia.');

    isTableLoading.value = true;
    showTableDialog.value = true;

    nextTick(() => {
        setTimeout(() => {
            tableLayerName.value = layer.get('name');
            const ignoredKeys = ['geometry', 'propertyId', 'geom', 'color', 'isText', 'texto', 'fontSize', 'fontFamily', 'textColor', 'strokeColor', 'customLabelField', 'tableName', 'isNew'];
            const allKeys = new Set();

            features.forEach(f => {
                Object.keys(f.getProperties()).forEach(k => {
                    if (!ignoredKeys.includes(k)) allKeys.add(k);
                });
            });
            tableColumns.value = Array.from(allKeys);

            const filters = {};
            tableColumns.value.forEach(col => filters[col] = '');
            tableFilters.value = filters;
            tableFeatures.value = features.map(f => ({ originalFeature: f, props: f.getProperties() }));

            isTableLoading.value = false;
        }, 150);
    });
};

const openTableExport = () => { showTableExportDialog.value = true; };

const confirmTableExport = () => {
    const featuresToExport = filteredTableFeatures.value.map(item => item.originalFeature.clone());
    featuresToExport.forEach(f => {
        f.set('color', tableExportData.value.fill);
        f.set('strokeColor', tableExportData.value.stroke);
    });

    const clonedSource = new VectorSource();
    clonedSource.addFeatures(featuresToExport);
    
    const newLayer = new VectorLayer({
        source: clonedSource,
        name: tableExportData.value.name,
        visible: true,
        zIndex: 1000
    });
    newLayer.set('name', tableExportData.value.name);
    
    globalMap.value.addLayer(newLayer);
    mapLayers.value.push(newLayer);
    
    showTableExportDialog.value = false;
    showTableDialog.value = false;
    openNotification('success', 'Camada gerada com sucesso.');
};

const toggleScaleVisibility = () => {
    if (globalScaleLine && globalScaleLine.element) {
        globalScaleLine.element.style.display = prePrintForm.showScale ? '' : 'none';
    }
};

const applyMapScale = () => {
    if (prePrintForm.scale !== 'current' && globalMap.value) {
        const targetScale = parseInt(prePrintForm.scale);
        const requiredResolution = targetScale / (39.37 * 90.714);
        globalMap.value.getView().setResolution(requiredResolution);
    }
};

const enterPreviewMode = () => {
    if (globalMap.value) globalMap.value.setTarget(null);
    prePrintForm.legendLayers = visibleLayerNames.value;
    isPreviewMode.value = true;
    
    setTimeout(() => {
        if (globalMap.value) {
            const previewContainer = document.getElementById('preview-map-container');
            if (previewContainer) {
                globalMap.value.setTarget(previewContainer);
                globalMap.value.updateSize();
                globalMap.value.renderSync(); 
                
                toggleScaleVisibility();
                applyMapScale();
                
                const controls = document.querySelectorAll('#preview-map-container .ol-zoom');
                controls.forEach(c => c.style.display = 'none');
            }
        }
    }, 150); 
};

const exitPreviewMode = () => {
    if (globalMap.value) globalMap.value.setTarget(null);
    isPreviewMode.value = false;
    
    setTimeout(() => {
        if (globalMap.value) {
            globalMap.value.setTarget(document.getElementById('map'));
            globalMap.value.updateSize();
        }
    }, 150);
};

const drawNorthArrow = (ctx, x, y, size) => {
    ctx.save();
    ctx.translate(x, y);

    ctx.beginPath(); ctx.moveTo(0, -size); ctx.lineTo(size/3, size); ctx.lineTo(0, size/1.5); ctx.lineTo(-size/3, size); ctx.closePath();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = '#333'; ctx.stroke();

    ctx.beginPath(); ctx.moveTo(0, -size); ctx.lineTo(size/3, size); ctx.lineTo(0, size/1.5); ctx.closePath();
    ctx.fillStyle = '#333'; ctx.fill();

    ctx.fillStyle = '#000'; ctx.font = `bold ${size/1.2}px Arial`; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.strokeStyle = 'white'; ctx.lineWidth = 3; ctx.strokeText('N', 0, -size - 5); ctx.fillText('N', 0, -size - 5);
    ctx.restore();
};

const downloadExport = async (format) => {
    isExporting.value = true;
    const legendImages = [];
    
    if (prePrintForm.showLegend && prePrintForm.legendLayers.length > 0) {
        const visibleLayersToPrint = mapLayers.value.filter(l => prePrintForm.legendLayers.includes(l.get('name')));
        for (const layer of visibleLayersToPrint) {
            const url = getLegendUrl(layer);
            let imgData = null;
            if (url) {
                try {
                    imgData = await new Promise((resolve) => {
                        const i = new Image();
                        i.crossOrigin = 'anonymous'; 
                        i.onload = () => resolve(i);
                        i.onerror = () => resolve(null);
                        i.src = url;
                    });
                } catch (e) {
                    console.error(e);
                }
            }
            legendImages.push({ 
                name: layer.get('name'), 
                img: imgData, 
                fallbackColor: layer.get('strokeColor') || '#333',
                fallbackFill: layer.get('fillColor') || 'rgba(0,0,0,0.3)'
            });
        }
    }

    setTimeout(() => {
        globalMap.value.once('postcompose', function (event) {
            try {
                const mapCanvas = document.createElement('canvas');
                const size = globalMap.value.getSize();
                mapCanvas.width = size[0];
                mapCanvas.height = size[1];
                const mapCtx = mapCanvas.getContext('2d');

                Array.prototype.forEach.call(
                    document.querySelectorAll('#preview-map-container .ol-layer canvas'),
                    function (canvas) {
                        if (canvas.width > 0) {
                            const opacity = canvas.parentNode.style.opacity;
                            mapCtx.globalAlpha = opacity === '' ? 1 : Number(opacity);
                            const transform = canvas.style.transform;
                            if (transform) {
                                const matrix = transform.match(/^matrix\(([^\(]*)\)$/)[1].split(',').map(Number);
                                mapCtx.setTransform(matrix[0], matrix[1], matrix[2], matrix[3], matrix[4], matrix[5]);
                            }
                            mapCtx.drawImage(canvas, 0, 0);
                        }
                    }
                );

                const margin = 50;
                const cw = mapCanvas.width + (margin * 2);
                let currentY = margin;
                const titleH = prePrintForm.topTitle ? 80 : 0;
                const stampH = prePrintForm.useStamp ? 200 : 0;
                
                const ch = mapCanvas.height + titleH + stampH + (margin * 2);

                const finalCanvas = document.createElement('canvas');
                finalCanvas.width = cw;
                finalCanvas.height = ch;
                const ctx = finalCanvas.getContext('2d');

                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, cw, ch);

                if (prePrintForm.topTitle) {
                    ctx.fillStyle = '#000';
                    ctx.font = 'bold 50px Arial';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'top';
                    ctx.fillText(prePrintForm.topTitle, cw / 2, currentY);
                    currentY += titleH;
                }

                ctx.strokeStyle = '#000';
                ctx.lineWidth = 4;
                ctx.strokeRect(margin, currentY, mapCanvas.width, mapCanvas.height);
                ctx.drawImage(mapCanvas, margin, currentY);

                if (prePrintForm.showLegend && legendImages.length > 0) {
                    const pixelRatio = window.devicePixelRatio || 1;
                    const maxLegendBoxW = Math.max(420, Math.floor(mapCanvas.width * 0.9));
                    const maxLegendBoxH = Math.max(260, Math.floor(mapCanvas.height * 0.75));
                    let legendTotalH = 40;
                    let maxLegendW = 150;
                    const legendRows = [];

                    legendImages.forEach(leg => {
                        const imageSize = leg.img
                            ? fitLegendImage(leg.img.width, leg.img.height, maxLegendBoxW - 24, Math.max(160, Math.floor(mapCanvas.height * 0.55)))
                            : { width: 20, height: 15 };
                        const rowH = leg.img ? imageSize.height : 20;
                        const rowW = leg.img ? Math.max(imageSize.width + 20, leg.name.length * 8 + 20) : 100;
                        if (rowW > maxLegendW) maxLegendW = rowW;
                        legendTotalH += rowH + 25;
                        legendRows.push({
                            ...leg,
                            drawWidth: imageSize.width,
                            drawHeight: imageSize.height,
                        });
                    });
                    maxLegendW = Math.min(maxLegendW, maxLegendBoxW);
                    legendTotalH = Math.min(legendTotalH, maxLegendBoxH);

                    const maxLegendX = margin + mapCanvas.width - maxLegendW;
                    const maxLegendY = currentY + mapCanvas.height - legendTotalH;
                    const legendX = Math.min(margin + (legendPreviewPos.x * pixelRatio), maxLegendX);
                    const legendY = Math.min(currentY + (legendPreviewPos.y * pixelRatio), maxLegendY);

                    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
                    ctx.fillRect(legendX, legendY, maxLegendW, legendTotalH);
                    ctx.strokeStyle = '#333';
                    ctx.lineWidth = 2;
                    ctx.strokeRect(legendX, legendY, maxLegendW, legendTotalH);

                    ctx.fillStyle = '#000';
                    ctx.font = 'bold 18px Arial';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'top';
                    ctx.fillText('LEGENDA', legendX + (maxLegendW / 2), legendY + 10);

                    ctx.beginPath();
                    ctx.moveTo(legendX + 10, legendY + 35);
                    ctx.lineTo(legendX + maxLegendW - 10, legendY + 35);
                    ctx.stroke();

                    let itemY = legendY + 45;
                    ctx.textAlign = 'left';
                    legendRows.forEach(leg => {
                        if (itemY > legendY + legendTotalH - 20) return;
                        ctx.fillStyle = '#000';
                        ctx.font = 'bold 14px Arial';
                        ctx.fillText(leg.name, legendX + 10, itemY);
                        itemY += 18;

                        if (leg.img) {
                            try {
                                ctx.drawImage(leg.img, legendX + 10, itemY, leg.drawWidth, leg.drawHeight);
                                itemY += leg.drawHeight + 10;
                            } catch(e) {
                                itemY += 20;
                            }
                        } else {
                            ctx.fillStyle = leg.fallbackFill; ctx.strokeStyle = leg.fallbackColor;
                            ctx.fillRect(legendX + 10, itemY, 20, 15); ctx.strokeRect(legendX + 10, itemY, 20, 15);
                            itemY += 25;
                        }
                    });
                }

                if (prePrintForm.showNorth) {
                    drawNorthArrow(ctx, margin + mapCanvas.width - 60, currentY + 70, 30);
                }

                currentY += mapCanvas.height;

                if (prePrintForm.useStamp) {
                    ctx.strokeStyle = '#000';
                    ctx.lineWidth = 4;
                    ctx.strokeRect(margin, currentY, mapCanvas.width, stampH);
                    
                    ctx.beginPath();
                    ctx.moveTo(margin + mapCanvas.width - 250, currentY);
                    ctx.lineTo(margin + mapCanvas.width - 250, currentY + stampH);
                    ctx.stroke();

                    ctx.fillStyle = '#000';
                    ctx.textAlign = 'left';
                    ctx.textBaseline = 'top';
                    
                    ctx.font = 'bold 36px Arial';
                    ctx.fillText(`TÍTULO: ${prePrintForm.mapTitle || 'Mapa Cadastral'}`, margin + 20, currentY + 30);
                    
                    ctx.font = '26px Arial';
                    ctx.fillText(`Descrição: ${prePrintForm.mapDescription || '-'}`, margin + 20, currentY + 80);
                    ctx.fillText(`Responsável: ${prePrintForm.responsible || '-'}`, margin + 20, currentY + 120);
                    
                    const dataStr = new Date().toLocaleDateString('pt-BR');
                    ctx.fillText(`Data: ${dataStr}   |   Obs: ${prePrintForm.observations || '-'}`, margin + 20, currentY + 160);

                    if (prePrintForm.logo && prePrintForm.logo.src) {
                        try {
                            const logoSize = stampH - 40;
                            ctx.drawImage(prePrintForm.logo, margin + mapCanvas.width - 210, currentY + 20, logoSize, logoSize);
                        } catch(e) {}
                    }
                }

                const filename = `${(prePrintForm.mapTitle || 'mapa').toLowerCase().replace(/ /g, '_')}`;
                if (format === 'pdf') {
                    const pdf = new jsPDF('landscape', 'mm', 'a4');
                    const a4W = 297, a4H = 210;
                    const aspect = cw / ch;
                    let printW = a4W, printH = a4W / aspect;
                    if (printH > a4H) { printH = a4H; printW = printH * aspect; }
                    
                    pdf.addImage(finalCanvas.toDataURL('image/jpeg', 1.0), 'JPEG', (a4W - printW)/2, (a4H - printH)/2, printW, printH);
                    pdf.save(`${filename}.pdf`);
                } else if (format === 'png') {
                    finalCanvas.toBlob((blob) => saveAs(blob, `${filename}.png`), 'image/png');
                } else if (format === 'jpg') {
                    finalCanvas.toBlob((blob) => saveAs(blob, `${filename}.jpg`), 'image/jpeg', 0.95);
                }

            } catch (err) {
                console.error(err);
                openNotification('error', 'Erro ao exportar mapa.');
            } finally {
                isExporting.value = false;
            }
        });
        
        globalMap.value.renderSync();
    }, 300);
};

const updateGlobalKeys = () => {
    globalAttributeKeys.value.clear();
    
    const extractKeysFromLayer = (layer) => {
        if (typeof layer.getLayers === 'function') {
            layer.getLayers().getArray().forEach(subLayer => extractKeysFromLayer(subLayer));
            return;
        }

        if (typeof layer.getSource === 'function') {
            const source = layer.getSource();
            if (!source || typeof source.getFeatures !== 'function') return;

            const feats = source.getFeatures();
            if (feats.length > 0) {
                Object.keys(feats[0].getProperties()).forEach(k => {
                    if (!['geometry', 'color', 'isText', 'textColor', 'fontSize', 'fontFamily', 'texto', 'isNew', 'strokeColor', 'customLabelField'].includes(k)) {
                        globalAttributeKeys.value.add(k);
                    }
                });
            }
        }
    };

    mapLayers.value.forEach(l => {
        extractKeysFromLayer(l);
    });
};

const loadFileLayers = async () => {
    documentsSource.clear();
    imagesSource.clear();

    try {
        const response = await window.axios.get('/api/map-actions/layers/file/points');
        const data = response.data.data;

        data.documents.forEach((document) => {
            const linkedGeometry = document.feature_geom
                ? new GeoJSON().readGeometry(JSON.parse(document.feature_geom), { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })
                : null;
            const feature = new GeoJSON().readFeature(
                {
                    type: 'Feature',
                    geometry: JSON.parse(document.geom),
                    properties: {
                        gid: document.gid, 
                        id: document.id,
                        name: document.name,
                        path: document.path,
                        type: 'document',
                        layer_id: document.layer_id,
                        geometry_gid: document.geometry_gid
                    }
                },
                { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
            );
            feature.set('linked_geometry', linkedGeometry);
            documentsSource.addFeature(feature);
        });
        documentsLayer.setSource(documentsSource);

        data.images.forEach(image => {
            const linkedGeometry = image.feature_geom
                ? new GeoJSON().readGeometry(JSON.parse(image.feature_geom), { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })
                : null;
            const feature = new GeoJSON().readFeature(
                {
                    type: 'Feature',
                    geometry: JSON.parse(image.geom),
                    properties: {
                        gid: image.gid,
                        id: image.id,
                        name: image.name,
                        path: image.path,
                        type: 'image',
                        layer_id: image.layer_id,
                        geometry_gid: image.geometry_gid
                    }
                },
                { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
            );
            feature.set('linked_geometry', linkedGeometry);
            imagesSource.addFeature(feature);
        });
        imagesLayer.setSource(imagesSource);
        
    } catch (error) {
        console.error("Erro ao carregar as camadas de arquivos:", error);
    }
};

onMounted(async () => {
    const loadGoogleMapsAPI = () => {
        if (!document.getElementById('google-maps-script')) {
            const script = document.createElement('script');
            script.id = 'google-maps-script';
            script.src = "https://maps.googleapis.com/maps/api/js?key=AIzaSyDbFtIdN_risSWYt-do2mKMZ8FxaTtf6wY&loading=async"
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }
    };
    loadGoogleMapsAPI();

    globalMap.value = markRaw(new Map({
        target: 'map',
        layers: [],
        view: new View(getDefaultMapView()),
        controls: defaultControls({ zoom: false, }),
    }));

    const isCoordinateInsideLinkedGeometry = (feature, coordinate) => {
        const linkedGeometry = feature.get('linked_geometry')
        return linkedGeometry?.intersectsCoordinate?.(coordinate) === true
    }

    globalMap.value.on('singleclick', function(evt) {
        if (activeTool.value === 'streetview') {
            const coords4326 = transform(evt.coordinate, 'EPSG:3857', 'EPSG:4326');
            showStreetView.value = true;
            svMarkerLayer.setVisible(true);

            if (typeof google !== 'undefined' && typeof google.maps !== 'undefined') {
                if (!svPanorama) {
                    svPanorama = new google.maps.StreetViewPanorama(document.getElementById('pano'), {
                        position: {lat: coords4326[1], lng: coords4326[0]}, pov: {heading: 0, pitch: 0}, zoom: 1
                    });
                    svPanorama.addListener('position_changed', () => {
                        const pos = svPanorama.getPosition();
                        if(pos) {
                            const newOlCoords = transform([pos.lng(), pos.lat()], 'EPSG:4326', 'EPSG:3857');
                            svFeature.getGeometry().setCoordinates(newOlCoords);
                            globalMap.value.getView().setCenter(newOlCoords);
                        }
                    });
                    svPanorama.addListener('pov_changed', () => { updateSvMarkerStyle(svPanorama.getPov().heading); });
                } else {
                    svPanorama.setPosition({lat: coords4326[1], lng: coords4326[0]});
                }
            } else { document.getElementById('pano').innerHTML = "<div style='padding:20px; color:red; font-weight:bold;'>Google Maps API não carregada.</div>"; }
        } else {
            selectedLayer.value = null;
            selectedFeature.value = null;
            documentsClicked.value = []
            imagesClicked.value = []

            documentsClicked.value = documentsSource
                .getFeatures()
                .filter(feature => isCoordinateInsideLinkedGeometry(feature, evt.coordinate))

            imagesClicked.value = imagesSource
                .getFeatures()
                .filter(feature => isCoordinateInsideLinkedGeometry(feature, evt.coordinate))

            if (!documentsClicked.value.length && !imagesClicked.value.length) {
                globalMap.value.forEachFeatureAtPixel(evt.pixel, (feature) => {
                    if (feature.get('type') === 'document') {
                        documentsClicked.value.push(feature)
                    } else if (feature.get('type') === 'image') {
                        imagesClicked.value.push(feature)
                    }
                }, {
                    hitTolerance: 10
                })
            }

            if (documentsClicked.value.length || imagesClicked.value.length) modalFilesOpen.value = true

            const feature = globalMap.value.getFeaturesAtPixel(evt.pixel)?.[0];
            if (!feature) {
                clearInteractions();
                panClick();
            }

            globalMap.value.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
                selectedFeature.value = feature;
                if (layer) {
                    selectedLayer.value = layer;
                } else {
                    selectedLayer.value = mapLayers.value.find(l => l.getSource && sourceHasFeature(l.getSource(), feature));
                }
                const color = feature.get('color');
                colorSelected.value = (!!color) ? color : '#3399CC';
                return true;
            }, { hitTolerance: 5 });
        }
    });

    try {
        const response = await window.axios.get('/api/maps/layers');
        const rawLayersData = response.data.data;

        const processTree = (layers) => {
            return layers
                .filter(ly => {
                    const hasName = ly.name && ly.name.trim() !== '';
                    return hasName;
                })
                .map(ly => {
                    return {
                        ...ly, 
                        active: false,
                        geoserver_url: GEO_SERVER_URL,
                    };
                    if (ly.layer_type === 'LayerGroup' && ly.layers) {
                        newItem.layers = processTree(ly.layers);
                    };
                })
                .filter(ly => ly.layer_type !== 'LayerGroup' || (ly.layers && ly.layers.length > 0));
        };

        const processedLayersData = processTree(rawLayersData);
        const layerPromises = processedLayersData.map(layerConfig => {
            return createEditableLayer(layerConfig, globalMap.value.getView().calculateExtent(globalMap.value.getSize()));
        });

        const layers = flattenLayerTree(await Promise.all(layerPromises));

        layers.forEach(layer => {
            if (!layer) return;

            const layerExists = globalMap.value.getLayers().getArray().find(l => l.get('name') === layer.get('name'));
            if (!layerExists) {
                configureEditableMapLayer(layer);

                if (layer.get('isMain') && (layer.get('name') === 'Lotes' || layer.get('name') === 'Edificações')) {
                    if (layer.get('name') === 'Lotes') {
                        globalMap.value.getLayers().insertAt(0, layer);
                        mapLayers.value.unshift(layer);
                    } else {
                        globalMap.value.getLayers().insertAt(1, layer);
                        mapLayers.value.splice(1, 0, layer);
                    }
                } else {
                    globalMap.value.addLayer(layer);
                    mapLayers.value.push(layer);
                }
            }
        });
        
        updateGlobalKeys();

    } catch (error) {
        console.error('Erro ao carregar camadas registradas:', error);
        openNotification('error', 'Falha ao conectar com a API de camadas.');
    }

    globalScaleLine = new ScaleLine({ bar: true, text: true, minWidth: 125 });
    globalMap.value.addControl(globalScaleLine);
    mapLayers.value.forEach(layer => layerStates.value.push({ ol_uid: layer.ol_uid, loaded: layer.get('isMain') }));

    globalMap.value.getLayers().insertAt(0, svMarkerLayer)
    globalMap.value.getLayers().insertAt(0, graticule)
    
    globalMap.value.addInteraction(undoInteraction);
    globalMap.value.addInteraction(selectInteraction);

    await loadFileLayers();
    globalMap.value.addLayer(documentsLayer);
    globalMap.value.addLayer(imagesLayer);

    globalMap.value.addLayer(annotationsLayer);

    loading.value = false;
});
</script>

<style scoped lang="scss">
.print-preview-layout {
    display: flex;
    gap: 12px;
    width: 100%;
    align-items: stretch;
}

.print-preview-layout > .print-spin {
    flex: 1 1 0;
    min-width: 0;
    width: auto !important;
    display: block;
}

.print-spin .ant-spin-nested-loading,
.print-spin .ant-spin-container {
    width: 100%;
    height: 100%;
    display: block;
}

.print-preview-board {
    background: #fff;
    padding: 20px;
    box-shadow: 0 0 10px rgba(0,0,0,0.2);
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
}

#preview-boundary {
    flex: 1 1 auto;
    min-height: 0;
    height: 70vh;

    position: relative;
    border: 2px solid #000;
    overflow: hidden;
    background: #e5e3df;
}

.draggable-print-legend {
    position: absolute;
    z-index: 120;
    width: max-content;
    max-width: none;
    max-height: 90%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 6px;
    border: 1px solid #bfbfbf;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 4px 12px rgba(0,0,0,0.18);
}

.print-legend-item {
    font-size: 12px;
    margin-bottom: 8px;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.print-legend-title {
    font-weight: bold;
    font-size: 12px;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.print-legend-image {
    max-width: 100%;
    max-height: 500px;
    object-fit: contain;
    display: block;
}

@media print {
    body * {
        visibility: hidden !important;
    }

    .print-preview-board,
    .print-preview-board * {
        visibility: visible !important;
    }

    .print-preview-board {
        position: fixed !important;
        left: 0 !important;
        top: 0 !important;
        width: 100vw !important;
        height: auto !important;
        padding: 0 !important;
        margin: 0 !important;
        box-shadow: none !important;
        background: white !important;
    }

    .print-preview-board h2 {
        page-break-after: avoid;
    }

    #preview-boundary {
        height: 75vh !important;
        min-height: 75vh !important;
    }

    .ant-modal,
    .ant-modal-content,
    .ant-modal-body {
        box-shadow: none !important;
        background: white !important;
    }

    .ant-modal-mask,
    .ant-modal-close,
    .ant-modal-footer,
    .modal-buttons,
    button {
        display: none !important;
    }

    @page {
        margin: 10mm;
    }
}

:deep(.floating-menu-container) { position: absolute; z-index: 1000; background-color: rgba(255, 255, 255, 0.95); border-radius: 8px; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3); display: flex; flex-direction: column; transition: all 0.3s ease; }

body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0; background-color: #f4f4f9; overflow: hidden; }
#map { width: 100vw; height: 100vh; position: relative; background-color: #e5e3df; }

.ant-form-item-label { padding-bottom: 2px !important; }

:deep(.btn-warning) { background-color: #ffc107 !important; color: #000 !important; &:hover { background-color: #e0a800 !important; } }
:deep(.btn-success) { background-color: #28a745 !important; &:hover { background-color: #218838 !important; } }
:deep(.btn-dark)  { background-color: #343a40 !important; &:hover { background-color: #23272b !important; } }
:deep(.btn-secondary) { background-color: #6c757d !important; &:hover { background-color: #5a6268 !important; } }
:deep(.btn-info) { background-color: #17a2b8 !important; &:hover { background-color: #138496 !important; } }
:deep(.btn-danger) { background-color: #dc3545 !important; color: white !important; border: 1px solid #dc3545 !important; &:hover { background-color: #c82333 !important;} }
:deep(.btn-active) { background-color: #28a745 !important; color: white !important; border-color: #28a745 !important; box-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.3) !important; }

#sv-modal { position: absolute; top: 20px; left: 80px; width: 400px; height: 300px; z-index: 2500; background: #fff; border-radius: 8px; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6); overflow: hidden; resize: both; border: 2px solid #343a40; }
#sv-header { background: #343a40; color: white; padding: 8px 10px; cursor: move; display: flex; justify-content: space-between; align-items: center; font-weight: bold; font-size: 14px; }
#pano { width: 100%; height: calc(100% - 35px); background: #eee; }

.custom-modal { position: absolute; z-index: 2000; top: 50%; left: 50%; transform: translate(-50%, -50%); background: #fff; padding: 20px; border-radius: 8px; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6); min-width: 350px; text-align: left; }
.custom-modal h3 { margin-top: 0; border-bottom: 1px solid #eee; padding-bottom: 10px; color: #333; font-size: 16px; }
.custom-modal label { font-weight: bold; font-size: 13px; color: #555; display: block; margin-top: 10px; }
.custom-modal input[type="text"], .custom-modal input[type="number"], .custom-modal select, .custom-modal input[type="file"] { width: 100%; padding: 8px; margin-top: 5px; margin-bottom: 5px; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }
.modal-buttons { display: flex; justify-content: space-between; gap: 10px; margin-top: 20px; }
.modal-buttons button { flex: 1; padding: 8px 15px; border: none; border-radius: 4px; cursor: pointer; font-weight: bold; color: white; }

.table-container { overflow: auto; max-height: 55vh; margin-top: 10px; border: 1px solid #eee; }
table { width: 100%; border-collapse: collapse; font-size: 12px; }
th, td { border: 1px solid #ddd; padding: 6px; text-align: left; white-space: nowrap; }
th { background-color: #f4f4f4; position: sticky; top: 0; z-index: 2; }
.tr-filter th { top: 28px; background-color: #e9ecef; z-index: 3; padding: 4px; }
.col-filter-input { width: 100%; padding: 4px; box-sizing: border-box; border: 1px solid #ccc; border-radius: 3px; font-size: 11px; }
tr:nth-child(even) { background-color: #f9f9f9; }
tr:hover { background-color: #f1f1f1; }

.map-files-carousel { flex: 1; width: 100%; min-height: 380px; max-height: 72vh; background: #f7f7f7; border-radius: 8px; padding: 8px; overflow: hidden; }
.map-files-carousel-wrap { margin-top: 4px; }
.map-files-carousel-inner { display: flex; align-items: center; gap: 6px; }
.map-files-carousel-arrow { color: #1677ff; flex-shrink: 0; }
.map-files-slide-inner { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; min-height: 320px; max-height: 65vh; }
.map-files-preview-img { max-width: 100%; max-height: 58vh; object-fit: contain; border-radius: 6px; }
.map-files-preview-pdf { width: 100%; height: 58vh; min-height: 280px; border: none; border-radius: 6px; }
.map-files-file-card { display: flex; flex-direction: column; align-items: center; gap: 10px; }
.map-files-file-icon { font-size: 42px; color: #1677ff; }
.map-files-file-type { color: #555; font-size: 14px; }
.map-files-slide-actions { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 8px; }
.map-files-slide-caption { text-align: center; color: #555; font-size: 12px; margin-top: 4px; }
.map-files-empty { color: #888; text-align: center; padding: 12px; }

/* Janelas Flutuantes das Legendas */
.floating-legend {
    position: absolute;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.3);
    overflow: hidden;
    border: 2px solid #343a40;
    min-width: 200px;
    max-width: 350px;
}

.legend-header {
    background: #343a40;
    color: white;
    padding: 6px 10px;
    font-weight: bold;
    font-size: 13px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: move;
}

.legend-body {
    padding: 10px;
    background: #fff;
    max-height: 400px;
    overflow-y: auto;
}

.legend-error {
    color: #dc3545;
    text-align: center;
    padding: 10px;
    font-weight: bold;
}

/* Painel Vetorização Avançada (CAD) */
.cad-panel {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.3);
    z-index: 1500;
    overflow: hidden;
    border: 2px solid #343a40;
    min-width: 320px;
}

.cad-header {
    background: #343a40;
    color: white;
    padding: 6px 12px;
    font-weight: bold;
    font-size: 13px;
    text-align: center;
}

.cad-body {
    display: flex;
    justify-content: space-around;
    padding: 10px;
    gap: 10px;
}

.cad-field {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.cad-field label {
    font-size: 11px;
    font-weight: bold;
    color: #333;
}

.cad-field input {
    width: 80px;
    padding: 4px;
    text-align: center;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 12px;
}
</style>
